/**
 * English strings. The only locale that ships.
 *
 * Everything user-facing goes through here from the first commit, so adding
 * Greek later is a second file rather than a refactor of every component.
 *
 * Copy rules, from the interface direction:
 *   - Name things by what the user controls. "Log time", not "Create entry".
 *   - The verb in the button is the verb in the toast. "Request leave" becomes
 *     "Leave requested".
 *   - Empty states say what to do next, not that there is nothing here.
 *   - Errors say what happened and how to fix it.
 */
export const en = {
  app: {
    name: 'Trackilero',
    loading: 'Loading',
  },

  nav: {
    dashboard: 'Dashboard',
    myTasks: 'My tasks',
    projects: 'Projects',
    time: 'Time',
    timesheet: 'Timesheet',
    planner: 'Planner',
    leave: 'Leave',
    capacity: 'Capacity',
    reports: 'Reports',
    settings: 'Settings',
    finance: 'Finance',
    goals: 'Goals',
    review: 'My week',
    clients: 'Clients',
    search: 'Search',
    notifications: 'Notifications',
    switchOrg: 'Switch organisation',
    team: 'Team',
    trash: 'Trash',
    favourites: 'Starred',
    groupMine: 'My work',
    groupWork: 'Work',
    groupPeople: 'People',
    groupInsights: 'Insights',
  },

  action: {
    newTask: 'New task',
    logTime: 'Log time',
    startTimer: 'Start timer',
    stopTimer: 'Stop timer',
    stop: 'Stop',
    newExpense: 'New expense',
    newInvoice: 'New invoice',
    newQuote: 'New quote',
    requestLeave: 'Request leave',
    save: 'Save',
    cancel: 'Cancel',
    more: 'More',
    delete: 'Delete',
    edit: 'Edit',
    approve: 'Approve',
    reject: 'Reject',
    export: 'Export CSV',
    retry: 'Try again',
    signIn: 'Sign in',
    signOut: 'Sign out',
    addSubtask: 'Add subtask',
    filter: 'Filter',
    clearFilters: 'Clear filters',
    loadMore: 'Load more',
    archive: 'Archive',
    showArchived: 'Show archived',
    hideArchived: 'Hide archived',
    remove: 'Remove',
    duplicate: 'Duplicate',
    restore: 'Restore',
    moveToTrash: 'Move to trash',
    deleteForever: 'Delete forever',
    add: 'Add',
    done: 'Done',
    close: 'Close',
    select: 'Select',
    import: 'Import',
    exportTasks: 'Export tasks',
    star: 'Star',
    unstar: 'Unstar',
    watch: 'Watch',
    unwatch: 'Stop watching',
    markAllRead: 'Mark all read',
    resend: 'Resend',
  },

  toast: {
    timerStarted: 'Timer started',
    timerStopped: 'Timer stopped, {duration} logged',
    timerPassed: 'Timer passed to {name}, {duration} logged',
    timeLogged: '{duration} logged',
    timeEntryDeleted: 'Entry deleted',
    taskCreated: 'Task created',
    taskUpdated: 'Task updated',
    leaveRequested: 'Leave requested',
    leaveApproved: 'Leave approved',
    leaveRejected: 'Leave rejected',
    leaveCancelled: 'Leave cancelled',
    settingsSaved: 'Settings saved',
    copied: 'Copied',
    projectCreated: 'Project created',
    projectUpdated: 'Project updated',
    projectArchived: 'Project archived',
    projectRestored: 'Project restored',
    clientCreated: 'Client created',
    clientUpdated: 'Client updated',
    clientArchived: 'Client archived',
    clientRestored: 'Client restored',
    brandingSaved: 'Branding saved',
    urlSaved: 'Workspace address updated',
    taskDuplicated: 'Task duplicated',
    taskTrashed: 'Task moved to trash',
    tasksTrashed: '{count} tasks moved to trash',
    taskRestored: 'Task restored',
    taskPurged: 'Task deleted for good',
    bulkUpdated: '{count} tasks updated',
    bulkUpdated_one: '1 task updated',
    starred: 'Starred',
    unstarred: 'Unstarred',
    watching: 'You will hear about every change',
    unwatched: 'You will no longer hear about changes',
    lineupAdded: 'Added to today',
    lineupRemoved: 'Removed from today',
    prefsSaved: 'Preferences saved',
    allRead: 'All notifications marked read',
    inviteResent: 'Invitation resent',
    holidayAdded: 'Holiday added',
    holidayRemoved: 'Holiday removed',
    tagsSaved: 'Tags saved',
    healthSaved: 'Project health updated',
    projectCloned: 'Project created from "{name}"',
    allowancesSet: 'Allowances set for {count} people',
    allowancesSet_one: 'Allowance set for 1 person',
    imported: '{count} tasks imported',
    importedWithTags: '{count} tasks imported \u00b7 {tags} new tags created',
    imported_one: '1 task imported',
    exported: 'Tasks exported',
  },

  status: {
    todo: 'To do',
    in_progress: 'In progress',
    blocked: 'Blocked',
    review: 'Review',
    done: 'Done',
  },

  priority: {
    none: 'No priority',
    low: 'Low',
    normal: 'Normal',
    high: 'High',
    urgent: 'Urgent',
  },

  tour: {
    step: 'Step {current} of {total}',
    next: 'Next',
    back: 'Back',
    done: 'Got it',
    skip: 'Skip',
    skipAll: 'Skip all guides',
    replayTitle: 'Screen guides',
    replayBody:
      'The first time you open a screen, a short walk points out what is on it. Bring them all back if you would like to see them again, or on a colleague\u2019s behalf.',
    replay: 'Show the guides again',
    replayed: 'The guides are back. Open a screen to see one.',
    dismissedAll: 'Guides off. You can bring them back in Settings.',

    dashboard: {
      intro: {
        title: 'A quick look round',
        body: 'Each screen explains itself the first time you open it, in a few steps like this one. Skip whenever you like \u2014 you can bring them back from Settings.',
      },
      sidebar: {
        title: 'Everything lives here',
        body: 'Your own work at the top, then the work itself, the people, and the numbers. Groups fold away, and starred projects sit at the bottom for the ones you are in every day.',
      },
      today: {
        title: 'What today looks like',
        body: 'What is due, what is running and what you logged, without opening anything. It fills in as you work rather than waiting to be set up.',
      },
      palette: {
        title: 'Jump to anything',
        body: 'Search tasks, projects and people, or run a command, without reaching for the sidebar. Press Cmd K \u2014 Ctrl K on Windows \u2014 from any screen.',
      },
      timer: {
        title: 'The timer follows you',
        body: 'Once a timer is running it sits here on every screen, showing the task and the time so far, so it is never quietly left on in a tab you closed.',
      },
    },

    myTasks: {
      intro: {
        title: 'Your work, across every project',
        body: 'Everything assigned to you in one list, whichever client or project it belongs to.',
      },
      lineup: {
        title: 'Line up a day\u2019s work',
        body: 'Pin the few tasks you mean to do today and they sit at the top, above everything else you have on. Nothing else changes \u2014 it is your list, not a status.',
      },
      list: {
        title: 'Depth is kept, not flattened',
        body: 'A subtask shows the task it belongs to, so a line here always tells you what larger piece of work it is part of.',
      },
      start: {
        title: 'Start the clock from the row',
        body: 'One press starts a timer on that task. Only one runs at a time, so starting a second stops the first rather than quietly double counting.',
      },
    },

    projects: {
      intro: {
        title: 'Every project you can see',
        body: 'Grouped by client, with hours and budget on each so you can tell how a project is going from the list.',
      },
      list: {
        title: 'Hours against the budget',
        body: 'The bar is time logged against the budget set for the project. It turns when a project passes 80%, and a manager hears about it once rather than every time.',
      },
      create: {
        title: 'Start a project',
        body: 'A project belongs to a client and holds the work. You can set the budget now or leave it \u2014 an unbudgeted project simply draws no bar.',
      },
    },

    project: {
      intro: {
        title: 'Inside a project',
        body: 'The task tree, the filters that cut through it, and the figures that say whether the job is going to pay.',
      },
      views: {
        title: 'List or board',
        body: 'The same tasks either way. The board moves work between statuses by dragging; the list is better once there are more than a screenful.',
      },
      filters: {
        title: 'Filters worth saving',
        body: 'Assignee, status, priority, tag, due date. A set you keep coming back to can be saved as a view, and an admin can share one with everybody.',
      },
      tree: {
        title: 'Three levels, counted properly',
        body: 'A task, its subtasks and theirs. Where a figure includes the work below it, it says so \u2014 own time and rolled-up time are never added together.',
      },
      budget: {
        title: 'What the project is worth',
        body: 'Quoted against earned and spent, and where both land at this rate. Only the finance roles see it.',
      },
    },

    task: {
      intro: {
        title: 'One piece of work',
        body: 'Everything about a task in one place: who has it, what it is waiting on, and where the time went.',
      },
      tracked: {
        title: 'Its time, and its subtasks\u2019 time',
        body: 'Two figures, never one. The first is what was logged on this task; the second includes everything beneath it, and says so.',
      },
      subtasks: {
        title: 'Break it down as far as three',
        body: 'A subtask is a task in every other respect \u2014 its own assignees, estimate and time. Completing the last one flags the parent rather than closing it for you.',
      },
      comments: {
        title: 'The conversation stays with the work',
        body: 'Mention somebody with @ and they hear about it. An edited or deleted comment leaves a mark, so the history never quietly changes.',
      },
    },

    time: {
      intro: {
        title: 'Logging time',
        body: 'Two ways in: run a timer while you work, or write down what you did afterwards.',
      },
      timer: {
        title: 'Start it and forget it',
        body: 'Pick a task and go. One timer at a time, running on the server rather than in this tab, so closing the browser does not stop it \u2014 and a timer left running for twelve hours is stopped and you are told.',
      },
      manual: {
        title: 'Or write it down after',
        body: 'A start, an end, and which task. Times are kept to the exact minute and never rounded to the nearest quarter of an hour.',
      },
      billable: {
        title: 'Billable is a choice per entry',
        body: 'The mark decides what reaches an invoice. Time that is real work but not chargeable \u2014 an internal review, a rework \u2014 stays in the totals and out of the bill.',
      },
    },

    timesheet: {
      intro: {
        title: 'Your week in one grid',
        body: 'Every entry laid out by day, to check the week before anybody reports on it.',
      },
      week: {
        title: 'Move between weeks',
        body: 'The daily and weekly totals follow. Entries on an issued invoice are closed and say so rather than silently refusing to change.',
      },
      grid: {
        title: 'Fix it here',
        body: 'Edit an entry in place \u2014 the task, the time, the note, whether it is billable. Every total downstream follows within moments.',
      },
    },

    planner: {
      intro: {
        title: 'Planning the day, not recording it',
        body: 'Blocks here are a plan. Nothing on this screen counts as time worked until you log it.',
      },
      grid: {
        title: 'Drag out the day',
        body: 'Drop a task onto an hour to say when you mean to do it. Your manager can see the shape of your week; nobody can move your blocks but you.',
      },
      sidebar: {
        title: 'What is waiting',
        body: 'Tasks with no home in the week yet, nearest deadline first, ready to be dragged into a slot.',
      },
    },

    capacity: {
      intro: {
        title: 'Whether the week fits',
        body: 'One row per person, one column per day, with leave already taken out.',
      },
      grid: {
        title: 'Hours actually available',
        body: 'Each person\u2019s working pattern, less approved leave and public holidays. Pending leave is shown apart, because it may still be refused.',
      },
      load: {
        title: 'Who is over, and by how much',
        body: 'Planned blocks and unplanned work due this week against what the person has. The label says the hours, so the colour is never carrying the meaning on its own.',
      },
      pipeline: {
        title: 'And if the quotes land',
        body: 'Open quotes laid over the weeks ahead at the likelihood somebody gave them. It answers the awkward question before you promise a date: does this still fit if they all come in?',
      },
    },

    leave: {
      intro: {
        title: 'Time off',
        body: 'Your balance, your requests, and who else is away.',
      },
      balance: {
        title: 'What is left, honestly',
        body: 'Allowance and carry-over, less what you have taken and what you have asked for. A pending request holds its days straight away, so two requests can never both fit into the same last week.',
      },
      request: {
        title: 'Pick the actual days',
        body: 'Weekdays are proposed and every day can be switched off, including a weekend day if you work it. Half days are half days.',
      },
      calendar: {
        title: 'Who else is off',
        body: 'The team\u2019s approved and pending leave together, so a week nobody is in is visible before it is booked.',
      },
    },

    reports: {
      intro: {
        title: 'Where the hours went',
        body: 'Reports read from figures kept up to date as time is logged, so they open at once however much history there is.',
      },
      picker: {
        title: 'Pick the question',
        body: 'Hours per person, per project, per task, billable against not, estimates against actuals. The columns change with the question.',
      },
      range: {
        title: 'Any range you like',
        body: 'This week, this month, or two dates of your own. Everything below follows the range, including what you export.',
      },
      rollup: {
        title: 'Rolled-up time is labelled',
        body: 'Where a figure includes subtasks it says so. Adding a rolled-up column to an own-time column would count the same hour twice, so the report never offers you the sum.',
      },
      export: {
        title: 'Take it away as CSV',
        body: 'The file is built on the server and matches what is on screen, range and filters included.',
      },
    },

  finance: {
      intro: {
        title: 'The money side',
        body: 'Rates, invoices, quotes and margin. Only an owner, an admin or an accountant sees any of this.',
      },
      tabs: {
        title: 'Four things, kept apart',
        body: 'What you charge, what you have billed, what you have offered, and what it cost. Each has its own tab because each has its own audience.',
      },
      stats: {
        title: 'Outstanding, overdue, earned',
        body: 'What clients owe, what is late, and what the month\u2019s hours generated at the resolved rates against what they cost.',
      },
      rates: {
        title: 'The most specific rate wins',
        body: 'A rate can be set for the workspace, a client, a project or a single task, and the narrowest one that applies is the one used.',
      },
    },

    team: {
      intro: {
        title: 'The team at a glance',
        body: 'Who is here, what they are working on and how their week is going.',
      },
      people: {
        title: 'One card each',
        body: 'This week\u2019s hours, what is running now, and who is away. A name opens what that person has on.',
      },
    },

    goals: {
      intro: {
        title: 'Something to aim at',
        body: 'A target for the team or a person \u2014 hours, billable hours, tasks finished, utilisation or invoiced.',
      },
      list: {
        title: 'Progress from real figures',
        body: 'Each goal reads the same rollups the reports do, so nothing has to be updated by hand and nothing can drift from what actually happened.',
      },
    },

    clients: {
      intro: {
        title: 'Who the work is for',
        body: 'Every client, their projects and their hours.',
      },
      list: {
        title: 'Hours all time and this month',
        body: 'Enough to see which clients the studio is actually spending its weeks on, which is rarely the order people expect.',
      },
      retainer: {
        title: 'Retainer hours, as they burn',
        body: 'A monthly allowance of hours, with what is left this month. A manager hears about it at 80% and again when it is gone.',
      },
    },
  },

  verify: {
    title: 'Check your email',
    lede: 'A six-digit code is on its way to {email}. Type it here and you are in. Not there in a minute? Check spam \u2014 first mails from a new sender often land there.',
    codeLabel: 'The code',
    confirm: 'Open my account',
    resend: 'Send a new code',
    resendIn: 'New code in {seconds}s',
    wrongAddress: 'Wrong address? Start over',
  },

  sample: {
    banner:
      'This workspace came with a worked example \u2014 a studio of four, six clients, three months of hours, and the invoices and quotes those hours produced \u2014 so every screen had something on it. Keep it while you try things out, or take it away before real work starts.',
    keep: 'Keep it for now',
    remove: 'Remove the example',
    confirmTitle: 'Remove the example?',
    confirmBody:
      'The {count} example documents go \u2014 the three invented colleagues, the clients, the work, the hours, the invoices and quotes \u2014 and the hours come off every total and report. Anything you have added yourself stays exactly as it is.',
    removed: 'The example is gone. {count} documents removed.',
    reseed: 'Put the example back',
    reseeded: 'The example is back: {count} documents.',
  },

  role: {
    label: 'Role',
    owner: 'Owner',
    admin: 'Admin',
    manager: 'Manager',
    member: 'Member',
    hint_member: 'Works on tasks, tracks time, requests leave.',
    hint_manager: 'Sees the whole team\u2019s work, time and leave; approves requests.',
    hint_admin: 'Runs the workspace: people, settings, billing, and everything below.',
    hint_accountant: 'Sees the money \u2014 rates, invoices, pay \u2014 and is otherwise an ordinary member.',
    accountant: 'Accountant',
    contractor: 'Contractor',
    hint_contractor: 'Outside the company: logs time on the work they are given, and sees no money and no reports.',
    viewer: 'Viewer',
    hint_viewer: 'Reads and never writes. For somebody who needs the numbers, not the buttons.',
    external: 'External',
    readOnly: 'Read-only',
    custom: 'Custom role',
    hint_custom: 'A role this workspace composed. What it may do is on the Roles tab.',
  },

  roles: {
    tab: 'Roles',
    title: 'Roles',
    subtitle: 'The built-in roles cover most people. Compose one when somebody needs a slice of the workspace and nothing else.',
    create: 'New role',
    edit: 'Edit',
    delete: 'Delete',
    empty: 'No composed roles yet. The built-in ones are on the People tab.',
    name: 'Name',
    namePlaceholder: 'HR',
    description: 'What it is for',
    descriptionPlaceholder: 'Decides leave and reads the reports.',
    startFrom: 'Start from',
    startFromHint: 'A starting point, not a limit. Change anything after picking one.',
    blank: 'Blank',
    allows: 'This role may',
    allowsHint: 'Everything not ticked is refused, in the rules and not only in the interface. Every role can still log its own time, comment, and see the work.',
    heldBy: 'Built-in: {roles}',
    holders: '{count} people',
    holdersOne: '1 person',
    holdersNone: 'Nobody yet',
    nothingPicked: 'Pick at least one thing this role may do.',
    saved: 'Role saved',
    deleted: 'Role deleted',
    deleteTitle: 'Delete {name}?',
    deleteBody: 'Anyone on this role keeps their account and their history, and drops to what an ordinary member can do. Their sign-in refreshes within a minute.',
    refreshNotice: 'Saving re-issues the sign-in of everyone holding this role. They will not be signed out.',
    adminOnly: 'Only an owner or admin can compose a role. This is deliberate: a role that can rewrite roles could grant itself everything.',
    pickRole: 'Composed roles',
    cap: {
      workManage: 'Create and edit clients, projects and templates',
      workManageHint: 'Shaping the work itself, not just doing it.',
      peopleManage: 'Invite people, deactivate them, and set their role',
      peopleManageHint: 'Cannot make anyone an admin — not even themselves.',
      leaveApprove: 'Decide any leave request',
      leaveApproveHint: 'As well as whoever is named as a person’s approver, never instead of them.',
      leaveAllowances: 'Set leave allowances and approvers',
      leaveAllowancesHint: 'How many days each person has, and who signs them off.',
      reportsAll: 'Read reports for the whole workspace',
      reportsAllHint: 'Without this, a person sees only their own figures.',
      timeViewAll: 'See everybody’s time entries',
      timeViewAllHint: 'The entries themselves, not only the totals in a report.',
      timeEditAny: 'Edit or delete somebody else’s time entry',
      timeEditAnyHint: 'Brings the line above with it: you cannot fix what you cannot see.',
      financeRead: 'See rates, invoices and pay',
      financeReadHint: 'Reading the money without being able to move it.',
      financeWrite: 'Issue invoices, set rates, run payroll',
      financeWriteHint: 'Brings the line above with it.',
      settingsManage: 'Change branding, workspace settings and domains',
      settingsManageHint: 'Not billing, and not the plan.',
      auditRead: 'Read the audit log',
      auditReadHint: 'Who changed what, and when.',
      exportFull: 'Export the whole workspace',
      exportFullHint: 'Every collection, as CSV.',
      clientLinks: 'Create and revoke client links',
      clientLinksHint: 'Money on those pages still needs the finance permissions.',
    },
    preset: {
      hr: 'HR',
      hrHint: 'Decides leave, sets allowances, reads the reports. No projects, no money.',
      projectLead: 'Project lead',
      projectLeadHint: 'A manager without people admin: runs the work and shares it with clients.',
      bookkeeper: 'Bookkeeper',
      bookkeeperHint: 'Reads the money and exports it. Changes nothing.',
      officeManager: 'Office manager',
      officeManagerHint: 'People, leave allowances and workspace settings. No money, no reports.',
      auditor: 'Auditor',
      auditorHint: 'Reads everything there is to read, and writes nothing.',
    },
  },

  task: {
    readOnlyNote: 'Only the people assigned to this task, whoever filed it, and managers can change it. You can still comment, log time and watch it.',
    depth0: 'Task',
    depth1: 'Subtask',
    depth2: 'Sub-subtask',
    unassigned: 'Unassigned',
    noDueDate: 'No due date',
    dueOn: 'Due {date}',
    overdue: 'Overdue',
    estimate: 'Estimate',
    calibrated: 'At your pace (×{factor}, from {sample} tasks): about {duration}',
    tracked: 'Tracked',
    trackedInclSubtasks: '{duration} (incl. subtasks)',
    ownTime: 'Own time',
    subtaskCount: '{count} subtasks',
    subtaskCount_one: '1 subtask',
    maxDepthReached: 'Sub-subtasks are the deepest level. Add this alongside instead.',
    startDate: 'Start',
    noStartDate: 'No start date',
    milestone: 'Milestone',
    milestoneHint: 'A point in the project rather than a piece of work. Shown with a flag.',
    quickSubtaskPlaceholder: 'What needs doing?',
    quickSubtaskHint: 'Enter adds and keeps going. Open the subtask afterwards for dates, people and the rest.',
    subtasksDone: '{done} of {total} subtasks done',
    progress: '{done}/{total}',
    more: 'More actions',
    setDue: 'Due date',
    setStart: 'Start date',
    setEstimate: 'Estimate',
    setPriority: 'Priority',
    editTitle: 'Rename',
    editDescription: 'Edit description',
    description: 'Description',
    descriptionPlaceholder: 'What needs doing. Plain text; links are clickable.',
    titleRequired: 'Give the task a title.',
    duplicateTitle: 'Duplicate this task?',
    duplicateBody:
      '"{title}" is copied with its subtasks, checklist, tags and links. Time logged, comments and history stay with the original.',
    duplicateSuffix: '(copy)',
    trashTitle: 'Move to trash?',
    trashBody:
      '"{title}" and its subtasks leave every list and board. Time logged against them stays in the reports. Anything in the trash can be restored for {days} days; after that it is deleted for good.',
    purgeTitle: 'Delete for good?',
    purgeBody: '"{title}" is removed permanently. Time logged against it stays in the reports under the task id.',
  },

  everywhere: {
    title: 'Everywhere',
    summary: '{count} workspaces · {running} running · {hours} today',
    summary_one: '1 workspace · {running} running · {hours} today',
    back: 'Back to {name}',
    today: '{hours} today',
    nothingRunning: 'No timer running here.',
    nothingDue: 'Nothing dated on your list here.',
    overdue: 'was due {date}',
    more: '… and {count} more dated tasks',
    noneTitle: 'You belong to no workspace yet',
    noneBody: 'Create one, or accept an invitation, and it appears here.',
  },

  honest: {
    checking: 'Working out the earliest honest date',
    earliestFor: 'Earliest honest date for {name}: {date}',
    useIt: 'Use {date}',
    fits: '{date} holds for {name}: {available} free by then, {demand} to do.',
    fitsShort: 'Holds for {name}',
    doesNotFit: '{date} is not honest for {name}: {available} free by then against {demand} to do, {queued} of it on work due sooner. Short by {short}.',
    earliestIs: 'The earliest date that holds is {date}.',
    beyondHorizon: 'Nothing in the next {days} days holds for {name}: {queued} is already due, before this {need}.',
    undated: '{minutes} more on undated tasks is not counted.',
    daysOff: '{count} days off on the way: weekends, leave, holidays.',
    daysOff_one: '1 day off on the way.',
    binding: 'Bound by {name}, who finishes last.',
    noTarget: '{name} has no work target, so eight hours a day is assumed.',
  },

  checklist: {
    title: 'Checklist',
    add: 'Add a step',
    placeholder: 'What has to happen',
    progress: '{done} of {total} done',
    empty: 'Break the task into steps. Each one can be ticked off.',
    remove: 'Remove "{text}"',
    full: 'A checklist holds up to {max} steps.',
  },

  tags: {
    title: 'Tags',
    none: 'No tags',
    add: 'Add tag',
    pick: 'Tags for {task}',
    filter: 'Tags',
    manage: 'Manage tags in Settings',
    settingsTitle: 'Tags',
    settingsHint:
      'One list for the whole workspace. A task can carry up to ten. Archiving a tag keeps it on the tasks that have it and takes it out of the picker.',
    name: 'Tag name',
    namePlaceholder: 'Design',
    nameRequired: 'Give the tag a name.',
    duplicate: 'A tag with that name already exists.',
    archive: 'Archive {name}',
    restore: 'Restore {name}',
    empty: 'No tags yet. Add the first one and it appears in every task\'s tag picker.',
    full: 'A task can carry up to {max} tags.',
  },

  links: {
    title: 'Links',
    add: 'Add link',
    url: 'Address',
    urlPlaceholder: 'https://',
    label: 'Label',
    labelPlaceholder: 'Optional',
    invalid: 'That is not a web address. It needs to start with http:// or https://.',
    empty: 'Attach a design file, a document, a ticket. Links only; nothing is uploaded.',
    remove: 'Remove {title}',
    full: 'A task holds up to {max} links.',
  },

  relations: {
    title: 'Relationships',
    waitingOn: 'Waiting on',
    related: 'Related',
    addWaiting: 'Add a task this one waits on',
    addRelated: 'Add a related task',
    pick: 'Pick a task',
    searchPlaceholder: 'Search tasks in this project',
    none: 'None',
    waitingCount: 'Waiting on {count}',
    waitingCount_one: 'Waiting on 1',
    hint: 'Informational only. Nothing stops a status change; the list says what to check first.',
    remove: 'Remove {title}',
    noMatch: 'No task in this project matches.',
  },

  watch: {
    title: 'Watchers',
    hint: 'Watchers hear about status changes, new comments and due-date moves, even when not assigned.',
    count: '{count} watching',
    count_one: '1 watching',
    you: 'You',
  },

  recur: {
    title: 'Repeat',
    none: 'Does not repeat',
    daily: 'Every day',
    weekly: 'Every week',
    monthly: 'Every month',
    hint: 'When this task is marked done, a fresh copy is created with the due date moved forward one period.',
    createdFrom: 'Created by repeating',
    next: 'Next occurrence',
    skipNext: 'Skip the next one',
    skipShort: 'skips next',
    endsAfter: 'More to come',
    remainingShort: '{count} left',
    controlsHint: 'Skipping advances the next copy by two periods, once. The count runs down with each copy; empty means until stopped.',
  },

  bulk: {
    selected: '{count} selected',
    selected_one: '1 selected',
    status: 'Set status',
    priority: 'Set priority',
    assign: 'Assign',
    tags: 'Add tag',
    archive: 'Archive',
    trash: 'Trash',
    clear: 'Clear selection',
    selectAll: 'Select all shown',
    confirmTrashTitle: 'Move {count} tasks to trash?',
    confirmTrashBody: 'Their subtasks go with them. Everything can be restored from the trash for {days} days.',
  },

  trash: {
    title: 'Trash',
    subtitle: '{count} tasks · deleted for good after {days} days',
    subtitle_one: '1 task · deleted for good after {days} days',
    emptyTitle: 'The trash is empty',
    emptyBody: 'Tasks a manager removes land here for {days} days before they are gone for good.',
    deletedOn: 'Deleted {date} by {name}',
    daysLeft: '{count} days left',
    daysLeft_one: '1 day left',
    project: 'Project',
  },

  io: {
    importTitle: 'Import tasks from CSV',
    importHint:
      'One row per task. Columns: Title, Description, Status, Priority, Due date, Start date, Estimate, Assignee email, Parent, Tags. An export from most work tools works once the headers are matched. Up to {max} rows at once.',
    importProject: 'Import into',
    importFile: 'CSV file',
    importPreview: '{count} rows ready · {nested} nested under a parent',
    importPreview_one: '1 row ready',
    importUnknownAssignee: '{count} assignee emails do not match anyone here and will be left unassigned.',
    matchPeople: 'Match people',
    matchPeopleHint: 'The file names its assignees; pick who each one is here. A name left as \u201cImport unassigned\u201d simply arrives without an assignee.',
    matchFor: 'Assignee for {name}',
    matchNobody: 'Import unassigned',
    importErrors: '{count} rows will be skipped:',
    importRun: 'Import {count} tasks',
    importRun_one: 'Import 1 task',
    importDone: 'Imported {count} tasks into {project}.',
    importEmpty: 'That file has no rows with a title.',
    importTooMany: 'That file has more than {max} rows. Split it and import in parts.',
    importBadFile: 'That does not read as a CSV. Export again and try once more.',
    exportHint: 'Every task in this project, one row each, with own time and time including subtasks in separate columns.',
    exportDone: 'Exported {count} tasks',
  },

  health: {
    title: 'Health',
    on_track: 'On track',
    at_risk: 'At risk',
    off_track: 'Off track',
    on_hold: 'On hold',
    unset: 'Not set',
    note: 'Note',
    notePlaceholder: 'One line on why. Optional.',
    updated: 'Updated {date} by {name}',
    set: 'Set health for {name}',
    hint: 'A fixed scale, the same in every workspace, so "at risk" always means the same thing.',
  },

  clone: {
    title: 'Create project from "{name}"',
    hint:
      'A new project with a copy of every task, subtask, checklist, tag and estimate. Statuses reset to To do; assignees, due dates, time logged and comments are not copied.',
    name: 'New project name',
    action: 'Create project',
    menu: 'Use as template',
  },

  person: {
    notFound: 'No such person',
    notFoundBody: 'They may have been removed from the workspace.',
    deactivated: 'Deactivated',
    edit: 'Edit member',
    openTasks: '{count} open tasks',
    noTasks: 'Nothing open is assigned to them.',
    approver: 'Leave approver',
    workDays: 'Works',
    monthlyTarget: 'Monthly target',
    since: 'Member since',
    hours: 'Hours',
    thisWeek: 'This week',
    thisMonth: 'This month',
    reports: 'Open the reports',
    leave: 'Time off coming up',
    noLeave: 'No approved leave ahead.',
  },

  team: {
    title: 'Team',
    subtitle: '{count} people',
    subtitle_one: '1 person',
    offToday: 'Off today',
    working: 'Working on',
    idle: 'Nothing running',
    thisWeek: 'This week',
    pendingLeave: 'Pending leave',
    nobodyOff: 'Everyone is in today.',
    holidayToday: 'Public holiday: {name}',
    activity: 'Latest activity',
    activityEmpty: 'Changes to tasks across the workspace show here as they happen.',
    noTarget: 'No target',
    since: 'since {time}',
    onTask: 'on {task}',
  },

  holidays: {
    title: 'Public holidays',
    hint:
      'Days the whole workspace has off. The leave picker leaves them unticked, capacity counts them as a day off for everyone, and the team calendar names them. They draw nothing from anyone\'s balance.',
    date: 'Date',
    name: 'Name',
    namePlaceholder: 'Christmas Day',
    add: 'Add holiday',
    remove: 'Remove {name}',
    empty: 'No holidays yet. Add the ones your team observes.',
    required: 'A holiday needs a date and a name.',
    duplicate: 'There is already a holiday on that date.',
    year: 'Year',
    inPicker: 'public holiday',
    count: '{count} holidays',
    count_one: '1 holiday',
  },

  allowances: {
    bulkTitle: 'Set allowances for everyone',
    bulkHint: 'One figure for every active person for the year and leave type you pick. People who already have an allowance for it are left as they are unless you tick "overwrite".',
    bulkAction: 'Set for {count} people',
    bulkAction_one: 'Set for 1 person',
    overwrite: 'Overwrite existing allowances',
    year: 'Year',
    type: 'Leave type',
    allocated: 'Days allocated',
    carried: 'Days carried over',
    skipped: '{count} already had one and were left alone.',
    skipped_one: '1 already had one and was left alone.',
  },

  prefs: {
    title: 'Notifications',
    hint:
      'Everything still appears in the app. This is only about email. Leave decisions and invitations always send, because missing one has consequences you cannot undo from inside the app.',
    emailFor: 'Email me about',
    type_task_assigned: 'Being assigned a task',
    type_task_completed: 'A task of mine being completed',
    type_mention: 'Being mentioned in a comment',
    type_task_watched: 'Changes to tasks I watch',
    type_task_due: 'Tasks of mine due today or overdue',
    type_timer_handed: 'A colleague passing me their timer',
    type_leave_request_submitted: 'Leave requests waiting on me',
    type_leave_cover: 'Covering for a colleague on leave',
    type_entry_notes_requested: 'Notes wanted on my entries before an invoice',
    type_project_budget: 'Project budget notices',
    type_capacity_changed: 'Capacity changes on my team',
    type_scheduled_report: 'Scheduled reports',
    type_year_end_balance: 'Year-end leave balances',
    type_client_retainer: 'Client retainer notices',
    type_invoice_overdue: 'Overdue invoices',
    type_quote_accepted: 'Quotes being accepted',
    reminderHour: 'Daily reminder hour',
    reminderHourHint: 'When the due-today note arrives, in your workspace\u2019s timezone.',
    reminderDefault: '08:00 (default)',
    dayBefore: 'Remind me the day before',
    dayBeforeHint: 'Tomorrow\u2019s due dates in today\u2019s note, so nothing arrives as a surprise.',
    digest: 'Weekly digest',
    digestHint: 'A Monday-morning email with your hours last week, what is due this week and anything overdue.',
    always: 'Always sent',
    saved: 'Preferences saved',
  },

  lineup: {
    title: 'Today',
    hint: 'Tasks you have lined up for today. Pin one from any row; clear it when it is done.',
    empty: 'Pin a task to today and it stays at the top of this list until you take it off.',
    pin: 'Pin {task} to today',
    unpin: 'Take {task} off today',
    full: 'Today holds up to {max} tasks.',
  },

  palette: {
    newExpense: 'New expense',
    newInvoice: 'New invoice',
    newQuote: 'New quote',
    placeholder: 'Search tasks, projects, people, or type a command',
    hint: 'Press ⌘K anywhere',
    tasks: 'Tasks',
    projects: 'Projects',
    clients: 'Clients',
    people: 'People',
    go: 'Go to',
    actions: 'Actions',
    noResults: 'Nothing matches. Try a different word.',
    startTimer: 'Start timer on {task}',
    newTask: 'New task',
    logTime: 'Log time',
    requestLeave: 'Request leave',
    toggleStar: 'Star or unstar the current project',
    shortcuts: 'Keyboard shortcuts',
    open: 'Open search',
  },

  shortcuts: {
    title: 'Keyboard shortcuts',
    hint: 'Press ? to see this list from any screen. Sequences are two keys pressed one after the other.',
    search: 'Search and commands',
    newTask: 'New task',
    goDashboard: 'Go to dashboard',
    goTasks: 'Go to my tasks',
    goProjects: 'Go to projects',
    goTime: 'Go to time',
    goPlanner: 'Go to planner',
    goLeave: 'Go to leave',
    goReports: 'Go to reports',
    goTeam: 'Go to team',
    goNotifications: 'Go to notifications',
    stopTimer: 'Stop the running timer',
    help: 'Show this list',
    escape: 'Close a dialog or menu',
  },

  activity: {
    title: 'Activity',
    feedEmpty: 'Changes to tasks across the workspace show here as they happen.',
  },

  favourites: {
    empty: 'Star a project and it stays here.',
  },

  branding: {
    workspace: 'Workspace',
    orgName: 'Organisation name',
    appName: 'App name',
    appNameHint: 'Shown in the sidebar and in outgoing email.',
    timezone: 'Timezone',
    timezoneHint:
      'One timezone for the whole organisation. Changing it affects which day future entries land on, not entries already logged.',
    searchTimezones: 'Search timezones',
    noTimezone: 'No timezone matches that. Try a city name.',
    emailOnAssign: 'Email on task assignment',
    emailOnAssignHint: 'In-app notifications are always sent. This adds an email.',

    address: 'Workspace address',
    addressHint:
      'The part of the link that names this workspace. Everyone here sees it in every URL they share.',
    addressLabel: 'Address',
    addressSave: 'Change address',
    addressReserved: '"{slug}" is reserved for the product itself. Pick another address.',
    addressWarning:
      'Links already shared that start with /{old} will stop working. Everyone will be moved to the new address the next time they open the app.',

    marks: 'Logo and icon',
    marksHint:
      'Uploaded images are downscaled in the browser and stored with the organisation. Use an image on your own site if you want it in outgoing email as well.',
    logo: 'Logo',
    logoHint: 'Replaces the wordmark in the sidebar. PNG or SVG with transparency works best.',
    favicon: 'Browser icon',
    faviconHint: 'Shown in the browser tab. A square image, 64px or larger.',
    none: 'None',
    upload: 'Upload',
    replace: 'Replace',
    uploading: 'Working…',
    useUrl: 'Use a URL instead',
    imageTooLarge: 'That image is too detailed to store. Try a simpler or smaller one.',

    palette: 'Colours',
    paletteHint:
      'Every colour applies as you change it, so you can see it against real data before saving.',
    presets: 'Start from',
    pickBy: 'Choose by',
    byStyle: 'Style',
    byIndustry: 'Industry',
    presetsShown: '{count} palettes',
    presetsShown_one: '1 palette',
    presetsHint: 'Pick a mood or your line of work and we propose a handful. Every palette passes the same legibility check before it is offered.',
    category: {
      monochromatic: 'Monochromatic',
      neutral: 'Neutral',
      tranquil: 'Tranquil',
      romantic: 'Romantic',
      playful: 'Playful',
      vibrant: 'Vibrant',
      seasonal: 'Seasonal',
    },
    categoryHint: {
      monochromatic: 'One hue through all its tones. Focused and consistent.',
      neutral: 'Greys, stones and creams. The quietest thing to look at for eight hours.',
      tranquil: 'Sea, sky and leaf. Calm without being flat.',
      romantic: 'Blush, dusk and petal. Warm and soft.',
      playful: 'Fruit, sun and candy. Light and quick.',
      vibrant: 'Saturated and mostly dark. For studios that want to be seen.',
      seasonal: 'A season in a palette, from spring green to harvest gold.',
    },
    industry: {
      agency: 'Agency or studio',
      software: 'Software and IT',
      finance: 'Finance and accounting',
      legal: 'Legal',
      health: 'Health and wellbeing',
      education: 'Education',
      hospitality: 'Hospitality and travel',
      construction: 'Construction and trades',
      retail: 'Retail and e-commerce',
      nonprofit: 'Non-profit',
    },
    fineTune: 'Fine-tune individual colours',
    adjusted:
      'Adjusted for legibility: {tokens}. The colour you picked did not have enough contrast against the surface behind it, so a darker or lighter version of the same hue is being used.',
    statusFixed:
      'Status colours are the same in every organisation: amber for a running timer, red for alert and overdue, green for normal. A support conversation has to mean the same thing everywhere, and status is carried by shape and label as well as colour.',
    unsaved: 'Unsaved changes',
    discard: 'Discard',
  },

  project: {
    new: 'New project',
    create: 'Create project',
    edit: 'Edit project',
    newHint: 'A project groups the tasks for one piece of work. Time is logged against its tasks.',
    name: 'Project name',
    namePlaceholder: 'Website redesign',
    client: 'Client',
    clientHint: 'Optional. Tasks created in this project inherit it.',
    noClient: 'No client',
    searchClients: 'Search clients',
    noClientsYet: 'No clients yet. Add one from the Clients screen.',
    description: 'Description',
    descriptionPlaceholder: 'What this project covers. Plain text.',
    color: 'Colour',
    colorHint: 'The dot beside the project everywhere it appears.',
    nameRequired: 'Give the project a name.',
    colorInvalid: 'That is not a six-digit hex colour. Try #2563EB.',
    emptyTitle: 'No projects yet',
    emptyBody:
      'A project groups the tasks for one piece of work. Create one and time can be logged against it.',
    count: '{count} projects',
    count_one: '1 project',
    archiveTitle: 'Archive this project?',
    archiveBody:
      'Archiving {name} hides it from the pickers. Its tasks, time and reports are untouched, and it can be restored.',
    restore: 'Restore',
    budget: 'Hour budget',
    budgetHint:
      'Optional. Managers are told when 80% is used and again when it is passed. Leave blank for none.',
    budgetPlaceholder: '40h',
    budgetInvalid: 'That is not a duration. Try 40h or 1:30.',
    budgetColumn: 'Budget',
    budgetUsed: '{tracked} of {budget}',
    budgetOver: 'Over by {amount}',
    budgetLeft: '{amount} left',
    budgetNone: 'No budget',
    budgetAllTime: 'all time',
    runwayOk: 'Runs out ~{date} at this pace',
    runwayOkHint: '{burn} a week over the last four',
    runwayIdle: 'No time in four weeks',
    runwayOver: 'Budget spent',
  },

  client: {
    new: 'New client',
    create: 'Create client',
    edit: 'Edit client',
    editNamed: 'Edit {name}',
    retainer: 'Retainer',
    retainerHours: 'Hours a month',
    retainerHint: 'Leave empty for no retainer. Tracked time on every project of this client counts against it.',
    retainerRollover: 'Unused hours roll over',
    retainerExpires: 'Unused hours expire',
    retainerNone: '—',
    retainerUsed: '{used} of {allowance}',
    retainerLeft: '{left} left',
    retainerOver: 'Over by {over}',
    retainerCarried: 'incl. {carried} carried',
    archiveNamed: 'Archive {name}',
    restoreNamed: 'Restore {name}',
    name: 'Client name',
    namePlaceholder: 'Acme Ltd',
    color: 'Colour',
    status: 'Status',
    active: 'Active',
    archived: 'Archived',
    nameRequired: 'Give the client a name.',
    count: '{count} clients',
    count_one: '1 client',
    projectCount: '{count} projects',
    projectCount_one: '1 project',
    noProjects: 'None',
    emptyTitle: 'No clients yet',
    emptyBody:
      'A client is a label projects hang off, so reports can be split by who the work was for.',
    archiveTitle: 'Archive this client?',
    archiveBody:
      'Archiving {name} hides it from the pickers. Projects already filed under it keep it, and it can be restored.',
    notFound: 'This client does not exist, or was deleted.',
    backToClients: 'Clients',
    hoursAllTime: 'Hours, all time',
    hoursAllTimeHint: 'Every minute logged on this client\u2019s projects',
    hoursThisMonth: 'Hours this month',
    outstandingHint: 'Issued invoices not yet paid',
    paidAllTime: 'Paid, all time',
    detailProjectsEmpty: 'No projects for this client yet. File one under it from the Projects screen.',
    detailQuotesEmpty: 'No quotes for this client yet.',
    detailInvoicesEmpty: 'No invoices for this client yet.',
    untitledQuote: 'Untitled quote',
  },

  time: {
    today: 'Today',
    thisWeek: 'This week',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    thisYear: 'This year',
    customRange: 'Custom range',
    billable: 'Billable',
    internal: 'Internal',
    running: 'Running',
    manual: 'Manual',
    timer: 'Timer',
    autoStopped: 'Auto-stopped at 12 hours',
    gap: 'Untracked {from} – {to}',
    locked: 'On an issued invoice; the record is closed',
    lockedShort: 'Invoiced',
    gapLogAs: 'Log as {task}',
    gapLogged: '{minutes} logged',
    whatAreYouWorkingOn: 'What are you working on?',
    duration: 'Duration',
    note: 'Note',
    startedAt: 'Started {time}',
    pass: 'Pass',
    passTimer: 'Pass the timer',
    passTitle: 'Pass the timer to a colleague',
    passLede: 'Your timer stops and {duration} is logged to you. Theirs starts this second on “{task}”, with your note, and they are told.',
    passTo: 'Who takes it',
    passPick: 'Pick a colleague',
    passNote: 'A word for them',
    passNoteHint: 'Replaces the note on the way over. Leave it as it is to pass your own.',
    passConfirm: 'Pass it',
    passNobody: 'Nobody else here can run a timer.',
    passedFrom: 'from {name}',
  },

  dashboard: {
    greeting: 'Good day, {name}',
    greetingAnon: 'Good day',
    studioMonth: 'Studio, {month} so far',
    yourMonth: 'You, {month} so far',
    utilisation: 'Utilisation',
    utilisationTarget: 'target {percent}%',
    capacityWeek: 'Capacity this week',
    ofTarget: '{tracked} of {target}',
    peopleActive: 'People active',
    weekByPerson: 'This week by person',
    yourWeek: 'Your week',
    projectsMonth: 'Projects this month',
    allProjects: 'All projects',
    noProjects: 'Create a project and the time logged against it appears here.',
    activeCount: '{count} active',
    runningNow: 'Running now',
    nobodyRunning: 'Nobody has a timer running.',
    startHint: 'Start a timer and it appears here and in the header.',
    attention: 'Needs attention',
    nothingWaiting: 'Nothing overdue and nothing waiting on you.',
    requestedLeave: '{name} requested leave',
    daysAwaiting: '{count} days, awaiting approval',
    daysAwaiting_one: '1 day, awaiting approval',
    clientCount: '{count} clients',
    clientCount_one: '1 client',
    projectCount: '{count} active projects',
    projectCount_one: '1 active project',
  },

  assign: {
    title: 'Assign to',
    assignSomeone: 'Assign this task to someone',
    editNamed: 'Assigned to {names}. Change who is on this task.',
    nobody: 'Nobody active to assign yet. Add people under Settings.',
    updated: 'Assignees updated',
    suspended: 'Suspended',
    suspendedCount: '{count} assignees are suspended and cannot open this task',
    suspendedCount_one: 'One assignee is suspended and cannot open this task',
  },

  downloads: {
    title: 'Downloads',
    hint:
      'The desktop timer is a small tray app for Mac and Windows. Sign in, find any task with a quick search and start or stop the timer without opening a browser tab.',
    features: 'See everything it does',
    mac: 'Trackilero Timer for Mac',
    windows: 'Trackilero Timer for Windows',
    macFile: 'macOS, zipped app',
    windowsFile: 'Windows, per-user installer',
    thisComputer: 'For this computer',
    download: 'Download',
    macNote: 'First launch on a Mac: right-click the app and choose Open.',
    windowsNote: 'If SmartScreen says it protected your PC, choose More info, then Run anyway.',
    unsigned:
      'Both builds are unsigned. First launch on a Mac: right-click the app and choose Open. On Windows, if SmartScreen says it protected your PC, choose More info, then Run anyway.',
    popupNew: 'New',
    popupTitle: 'Track time from your menu bar',
    popupBody: 'A small tray app for your computer: sign in once, search any task and start or stop the timer without opening a browser tab.',
    popupDownloadMac: 'Download for Mac',
    popupDownloadWindows: 'Download for Windows',
    popupAlsoMac: 'Also for Mac',
    popupAlsoWindows: 'Also for Windows',
    popupLater: 'Find it any time in your profile, under Downloads.',
    popupClose: 'Close',
  },

  profile: {
    tab: 'Profile',
    title: 'Your profile',
    hint: 'How your name appears to everyone else in this workspace. Time already logged keeps the name it was recorded with.',
    displayName: 'Display name',
    displayNamePlaceholder: 'John Doe',
    billableTarget: 'Billable target',
    billableTargetHint: 'The share of your tracked time you aim to bill, as a percentage. Shown against the actual on the dashboard.',
    jobTitle: 'Job title',
    jobTitlePlaceholder: 'Optional',
    notificationsTab: 'Notifications',
    email: 'Email',
    emailHint: 'Set when the account was created and not editable here.',
    nameRequired: 'Give yourself a name of at least two characters.',
    saved: 'Profile saved',
    hours: 'Hours you work',
    hoursHint:
      'What a normal day, week and month look like for you. Anything past these turns red on your timesheet, so an overrun is visible rather than something you find out about later.',
    perDay: 'Per day',
    perWeek: 'Per week',
    perMonth: 'Per month',
    hoursEmpty:
      'Leave any of them empty for no target. A week is not always five days and a month is not four weeks, so each is set on its own.',
    badHours: 'Enter hours like 8h, 7.5h or 450. Leave a box empty for no target.',
    workDays: 'Working days',
    workDaysHint: 'The days you are expected to work. Capacity counts only these.',
    availability: 'Availability',
    availabilityHint:
      'Leave both empty if you are here indefinitely. A contractor with an end date stops being planned for after it.',
    availableFrom: 'Available from',
    availableUntil: 'Available until',
    badWindow: 'The end of the availability window is before its start.',
    noWorkDays: 'Pick at least one working day, or nothing can be planned.',
  },

  refunds: {
    title: 'Refunds and billing terms',
    effective: 'Effective {date}',
    lede:
      'What a subscription costs, how seats and changes are charged, how to cancel, and when we refund. Written to match exactly what the billing screen does.',
  },

  privacy: {
    title: 'Privacy policy',
    effective: 'Effective {date}',
    lede:
      'Trackilero holds the work, time and leave records of small teams, and can read a calendar if you ask it to. This page says exactly what is collected, why, where it is kept, who can see it, and how to get it back or removed. It is long because it is specific.',
    contents: 'Contents',
    signIn: 'Sign in',
  },

  consent: {
    title: 'Cookies',
    body:
      'Trackilero needs a little storage to keep you signed in, and that part is always on. If you allow it, Google Analytics also counts visits to the site, against a random id, never your account or your work. Payment screens use Stripe\u2019s fraud-prevention cookies either way.',
    link: 'The full list',
    acceptAll: 'Accept all',
    onlyNecessary: 'Only necessary',
    choices: 'Choices',
    save: 'Save choice',
    necessaryNote: 'Signing in, your preferences, Stripe on payment screens. Always on.',
    analyticsNote: 'Google Analytics visit counting. Off until you allow it.',
  },

  org: {
    trialTitle: 'You are on the Team trial',
    trialBody: 'Everything in Team, free for {days} days, no card. When it ends the workspace moves to the Starter plan \u2014 nothing is deleted. If you would rather begin small, start on Starter now and upgrade whenever you like.',
    trialKeep: 'Keep the trial',
    trialDecline: 'Start on Starter instead',
    trialDeclining: 'Switching\u2026',
    trialDeclined: 'You are on Starter. Upgrade any time from Settings \u2192 Billing.',
    slugNotice: 'Your workspace address is trackilero.com/{slug} \u2014 the name itself was taken or does not shorten. Change it any time in Settings.',
  },

  cookies: {
    title: 'Cookie policy',
    effective: 'Effective {date}',
    lede:
      'Everything Trackilero keeps in your browser is listed below, item by item, with what each one is for and how long it lasts: the storage that signs you in, the preferences it remembers, Stripe\u2019s fraud-prevention cookies on payment screens, and the Google Analytics measurement of site visits. The list is generated from the product itself and checked against a real browser after every release.',
    lastChecked: 'Last checked against the product on {date}.',
    neverChecked: 'Not yet checked against a live browser.',
    whatTitle: 'What a cookie is, and what else this covers',
    whatBody:
      'A cookie is a small text record a website asks your browser to keep and send back. Browsers also offer other storage — localStorage, sessionStorage and IndexedDB — which does the same job without being sent with every request. European rules (the ePrivacy Directive, article 5(3)) treat all of these alike, so this page lists all of them.',
    categoriesTitle: 'Categories',
    necessary: 'Strictly necessary',
    necessaryBody: 'Needed for the product to work at all: keeping you signed in, completing a sign-in link. No consent is required for these and they cannot be switched off without breaking the service.',
    preference: 'Preferences',
    preferenceBody: 'Remember a choice you made, such as which workspace or view you had open. Harmless to clear; the product simply asks again.',
    analytics: 'Analytics',
    analyticsBody: 'Google Analytics counts visits to the site: which pages are opened, roughly from where, against a random client id. It is not connected to your account, and nothing you enter \u2014 tasks, time, leave \u2014 is sent to it.',
    marketing: 'Marketing',
    marketingBody: 'None, and no plans.',
    inventoryTitle: 'Every item, in full',
    colName: 'Name',
    colKind: 'Type',
    colProvider: 'Set by',
    colPurpose: 'Purpose',
    colDuration: 'Lasts',
    colControl: 'Your control',
    thirdTitle: 'Third parties',
    thirdBody:
      'The application is served by Google Firebase Hosting and talks to Firebase Authentication, Firestore and Cloud Functions in the EU; the Firebase client library creates the IndexedDB entries listed above. The Google tag (googletagmanager.com) loads the Google Analytics measurement and sets the _ga cookies. Stripe\'s library loads only on screens with a payment form, renders the card form and sets its fraud-prevention cookies. If you connect a Google Calendar, the consent screen you pass through is on google.com and is governed by Google\'s own cookie policy, not this one. No other company\'s code runs on this site.',
    controlTitle: 'Clearing and controlling',
    controlBody:
      'Signing out removes the sign-in record. Everything else can be cleared with your browser\'s "clear site data" for trackilero.com; the product keeps working and asks for your preferences again. Blocking storage entirely in the browser will prevent sign-in, because the signed token has nowhere to live.',
    unknownTitle: 'Found in the last check and not yet described',
    unknownBody: 'These appeared in a browser during the last check and are being documented. They are listed so that nothing is hidden in the meantime.',
    changesTitle: 'Changes',
    changesBody:
      'When the product starts or stops storing something, this page changes with it and the date at the top is updated. A material change — analytics, a new third party — would also be announced to every workspace owner before it takes effect, and asked for where the law requires consent.',
    contact: 'Questions go to {email}.',
    changeChoice: 'Change your cookie choice',
    kind_cookie: 'Cookie',
    kind_localStorage: 'Local storage',
    kind_sessionStorage: 'Session storage',
    kind_indexedDB: 'IndexedDB',
    kind_script: 'Third-party script',
  },

  people: {
    edit: 'Edit',
    editTitle: 'Edit {name}',
    editHint: 'Role, approver and the hours they work. Time already logged keeps the name it was recorded with.',
    suspend: 'Suspend',
    suspendTitle: 'Suspend {name}?',
    suspendBody:
      'They will not be able to sign in to this workspace, and they come off any task that is still open — work nobody can see is worse than work nobody is doing. Finished tasks keep their name, and every time entry, comment and leave record stays exactly as it is. You can reactivate them at any time.',
    suspended: '{name} suspended',
    reactivate: 'Reactivate',
    reactivated: '{name} reactivated',
    remove: 'Remove',
    removeTitle: 'Remove {name}?',
    removeBody:
      'Their login to this workspace is removed and they cannot be reactivated, and they come off any task that is still open. Their history stays: time, comments, leave and finished tasks keep their name. If you only want to stop them signing in for a while, suspend them instead.',
    removed: '{name} removed',
    unassigned: 'taken off {count} open tasks',
    unassigned_one: 'taken off 1 open task',
    saved: 'Changes saved',
    approver: 'Leave approver',
    noApprover: 'No approver',
    ownRole: 'You cannot change your own role.',
    ownerRole: 'Transfer ownership from Maintenance to change the owner.',
    statusActive: 'Active',
    statusSuspended: 'Suspended',
  },

  admin: {
    title: 'Platform',
    subtitle: '{count} workspaces',
    notYours: 'This screen is for the platform owner',
    grantHint:
      'Access comes from a custom auth claim rather than a second password, so there is no credential in the app to leak. Grant it once, with the Firebase Admin SDK:',
    grantThen: 'Then sign out and back in, so the new claim reaches this browser.',
    integrations: 'Integrations',
    integrationsHint:
      'The keys the product sends mail and reads calendars with. Stored where no browser can read them; a saved key shows only its last four characters. Leave a field blank to keep what is set; clear it to remove a key.',
    zeptoToken: 'ZeptoMail send token',
    stripeTitle: 'Stripe',
    stripeHint:
      'Two sets of keys, one switch. The product charges with whichever set is active; the other stays saved, so a sandbox round of testing and the return to live are one click each, not seven pastes. Both webhooks stay registered at Stripe; only the active signing secret is checked.',
    stripeSetup: 'Set up Stripe for this mode',
    stripeSetupRunning: 'Setting up…',
    stripeSetupHint:
      'Paste the secret and publishable keys, then press this. With the secret key it finds or creates the products and the four per-seat prices, registers the webhook for this site and keeps its signing secret, and makes the customer portal — and fills the fields below itself, so the ids always belong to the same Stripe environment as the key.',
    stripeSetupDone: 'Stripe is set up for this mode',
    stripeLive: 'Live',
    stripeSandbox: 'Sandbox',
    stripeActive: 'Active — the product charges with these',
    switchToLive: 'Switch to live',
    switchToSandbox: 'Switch to sandbox',
    stripeSwitched: 'Stripe is now on {mode}. Payments pick it up within half a minute.',
    stripeSecretKey: 'Stripe secret key (sk_…)',
    stripePublishableKey: 'Stripe publishable key (pk_…) — the payment form in the browser uses it',
    stripeWebhookSecret: 'Stripe webhook signing secret (whsec_…) — endpoint /stripe/webhook',
    stripePriceTeamMonthly: 'Stripe price id · Team, monthly, per seat',
    stripePriceTeamYearly: 'Stripe price id · Team, yearly, per seat',
    stripePriceBusinessMonthly: 'Stripe price id · Business, monthly, per seat',
    stripePriceBusinessYearly: 'Stripe price id · Business, yearly, per seat',
    zeptoFrom: 'Sender address',
    googleClientId: 'Google OAuth client ID',
    tabPlatform: 'Platform',
    tabSubscriptions: 'Subscriptions',
    subsModeLive: 'Live Stripe. What you do here moves real money.',
    subsModeSandbox: 'Sandbox Stripe. Nothing here is real; switch on the Platform tab.',
    subsNotConfigured: 'Stripe keys for this mode are missing',
    subsRefresh: 'Re-read from Stripe',
    subsPeriodEnd: 'Period ends',
    subsSeats: '{seats} · {people} in use',
    subsOtherMode: 'billing record on the other mode',
    subsPaused: 'paused',
    subsEnding: 'ends at period close',
    subsStatus_active: 'Active',
    subsStatus_trialing: 'Trialing',
    subsStatus_past_due: 'Past due',
    subsStatus_unpaid: 'Unpaid',
    subsStatus_canceled: 'Cancelled',
    subsStatus_incomplete: 'Payment not finished',
    subsStatus_incomplete_expired: 'Expired',
    subsStatus_paused: 'Paused',
    subsStatus_none: 'No subscription',
    subsCancelEnd: 'Cancel at period end',
    subsCancelNow: 'Cancel now',
    subsPause: 'Suspend billing',
    subsUnpause: 'Resume billing',
    subsSync: 'Sync from Stripe',
    subsChangePlan: 'Change plan',
    subsOrgPlanDefault: 'Plan: automatic',
    subsOrgPlan: 'Plan: {plan}',
    subsOpenStripe: 'Open in Stripe',
    subsNone: 'Never subscribed',
    subActionDone: '{name} updated',
    subsConfirm_cancelAtPeriodEnd: 'Cancel {name} at the period end?',
    subsConfirmBody_cancelAtPeriodEnd: 'The plan runs to the end of what is paid, then the workspace moves to Starter. Can be resumed before then.',
    subsConfirmLabel_cancelAtPeriodEnd: 'Cancel at period end',
    subsConfirm_cancelNow: 'Cancel {name} immediately?',
    subsConfirmBody_cancelNow: 'The subscription ends now with a prorated credit for the unused days on the customer balance. The workspace moves to Starter at once. Refund from Stripe separately if owed.',
    subsConfirmLabel_cancelNow: 'Cancel now',
    subsConfirm_pause: 'Suspend billing for {name}?',
    subsConfirmBody_pause: 'Collection pauses: invoices are voided and nothing is charged until resumed. The plan and seats stay as they are.',
    subsConfirmLabel_pause: 'Suspend billing',
    headTagsTitle: 'Head tags',
    headTagsHint:
      'Raw HTML placed inside <head> on every page, for every visitor, before sign-in: Google Analytics, Meta Pixel, site verification tags. Scripts run as pasted, so check them. A tracker added here is a third party the cookie page must describe — update it, and the "no other company\'s code" line, before relying on this.',
    headTags: 'Tags',
    headTagsPlaceholder: '<!-- Google tag (gtag.js) -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXX"></script>',
    saveHeadTags: 'Save tags',
    headTagsSaved: 'Head tags saved. They take effect on the next page load.',
    cookiesTitle: 'Cookies and browser storage',
    cookiesHint:
      'Reads what this browser is holding for the site right now — cookie names, local storage keys, IndexedDB databases, third-party scripts — and checks each against the inventory the cookie page is written from. Anything unknown is flagged; publish the scan to put the check date and any unknowns on the public page.',
    scan: 'Scan this browser',
    scanning: 'Scanning…',
    publishScan: 'Publish to the cookie page',
    scanPublished: 'Scan published',
    scanEmpty: 'Nothing found. Sign in and use the product in this browser first, then scan.',
    scanKnown: '{count} known',
    scanUnknown: '{count} unknown',
    scanNoIdb: 'This browser cannot list IndexedDB databases; scan in Chrome or Safari for the full picture.',
    known: 'Known',
    unknown: 'Unknown',
    lastPublished: 'Last published {date}',
    openCookiePage: 'Open the cookie page',
    googleRedirect: 'Authorised redirect URI',
    googleRedirectHint:
      'Add exactly this to the OAuth client in Google Cloud, under Credentials. A "redirect_uri_mismatch" on connecting a calendar means the client is missing it, or the product runs on a different address than APP_BASE_URL says.',
    googleClientSecret: 'Google OAuth client secret',
    setKey: 'Set',
    clearKey: 'Clear',
    keySet: 'Set to {masked}',
    keyUnset: 'Not set',
    keysSaved: 'Keys saved',
    workspaces: 'Workspaces',
    workspace: 'Workspace',
    address: 'Address',
    plan: 'Plan',
    planHint: 'Unlocks features and the seat limit at once. Blank means Team while on trial, then Starter.',
    planDefault: 'Not set',
    trialEnds: 'Trial ends',
    status: 'Status',
    status_trial: 'Trial',
    status_active: 'Active',
    status_suspended: 'Suspended',
    statusSet: '{name} updated',
    planSet: '{name} is now on {plan}',
    people: '{count} people',
    people_one: '1 person',
    showPeople: 'Show people',
    hidePeople: 'Hide people',
    deactivated: 'deactivated',
    suspend: 'Suspend',
    activate: 'Activate',
    delete: 'Delete workspace',
    deleteTitle: 'Delete {name}?',
    deleteBody:
      'Every task, time entry, report, leave record and member of this workspace is removed, permanently, and its people lose access at once. Type the address to confirm.',
    deleteConfirmLabel: 'Workspace address',
    deleted: '{name} deleted',
    noOrgsTitle: 'No workspaces yet',
    noOrgsBody: 'The first one appears here as soon as somebody creates it.',
  },

  calendar: {
    connect: 'Connect calendar',
    connected: 'Calendar connected',
    disconnect: 'Disconnect',
    disconnected: 'Calendar disconnected',
    readOnly: 'Read only. Trackilero never writes to your calendar.',
    lane: 'Calendar',
    declined: 'Declined',
    connectedToast: 'Calendar connected',
    failed: 'That did not complete. Try connecting again.',
    expired: 'That took too long. Try connecting again.',
    retry: 'Google did not return a lasting permission. Try connecting again.',
    declinedToast: 'Calendar not connected',
    myCalendars: 'Calendars',
    syncing: 'Synced from {email}',
    syncNote: 'Read every minute while the planner is open. Untick to hide a calendar; remove to stop reading it.',
    show: 'Show {name}',
    hide: 'Hide {name}',
    remove: 'Remove {name}',
    removed: '{name} removed',
    unassigned: 'taken off {count} open tasks',
    unassigned_one: 'taken off 1 open task',
    restore: 'Add {name} back',
    restored: '{name} added',
    addCalendar: 'Add a calendar',
    addAccount: 'Add Google account',
    removeAccount: 'Remove',
    removeAccountBody: 'Stop reading every calendar on {email} and forget its permission. You can add it again at any time.',
    removedCount: '{count} removed calendars',
    removedCount_one: '1 removed calendar',
    addBack: 'Add back',
    noneRemoved: 'Every calendar on the account is listed.',
    disconnectAccount: 'Disconnect account',
    notConnectedTitle: 'No calendar yet',
    notConnectedBody: 'Connect your Google Calendar to see meetings and calls beside your plan.',
    loadFailed: 'Google did not answer. The connection may need to be made again.',
  },

  planner: {
    title: 'Planner',
    waiting: 'Waiting on you',
    planned: '{duration} planned',
    nothingTitle: 'Nothing assigned to you',
    nothingBody: 'Tasks land here when someone assigns you to them, ready to be dragged onto a day.',
    scheduled: '{task} scheduled',
    dueHere: '{task} is due this day',
    startBlock: 'Start a timer on {task}',
    removeBlock: 'Remove {task} from the plan',
    resizeBlock: 'Change how long {task} is planned for',
    allDay: 'All day',
    makeAllDay: 'Make {task} an all-day block',
    makeTimed: 'Give {task} a time again',
    extendBlock: 'Change how many days {task} spans',
    hint: 'Drag a task onto an hour to plan it, or onto the all-day lane for a whole day — then pull its right edge across the days it needs. A plan is not tracked time; press play on a block to start the timer.',
    today: 'Today',
    viewDay: 'Day',
    viewWeek: 'Week',
    viewMonth: 'Month',
    view: 'View',
    previous: 'Previous',
    next: 'Next',
    more: '{count} more',
    openDay: 'Open {date}',
    miniMonth: 'Pick a date',
    tasks: 'Tasks',
    now: 'Now',
  },

  landing: {
    heroNote: 'Free for up to {seats} people, then {price} a person a month. Hosted in the EU.',
    shotTimesheet: 'The weekly timesheet in Trackilero, with a week of hours logged against client projects',
    shotLeave: 'The team leave calendar, showing who is away over the next two weeks',
    shotFeatures: 'Settings, Features: the Simple, Standard and Full setups, and a switch for each area',
    timeTitle: 'Log time the way you work.',
    timeBody: 'Start the timer when you begin something, or fill in the week on Friday. Minutes are kept exactly as logged and never rounded, and a timer someone forgets stops itself after twelve hours.',
    timeB1: 'A weekly grid you type hours into, with a note and a billable flag on each entry.',
    timeB2: 'Start a timer from any task, from search or with a shortcut.',
    timeB3: 'A desktop timer for Mac and Windows that sits in the menu bar.',
    leaveTitle: 'Book time off, and see who is away.',
    leaveBody: 'People pick the exact days they want off, half days included. Their approver says yes, no, or yes to some of the days, and the team calendar shows who is away.',
    leaveB1: 'Days come off the balance as soon as a request goes in, so two requests cannot use the same days.',
    leaveB2: 'Allowances per person, carry-over rules, and a reminder before the year ends.',
    leaveB3: 'Leave is on every plan, including the free one.',
    setupTitle: 'Only what your team uses.',
    setupBody: 'A new workspace starts on Simple: time, a calendar of the week, and leave. Tasks, clients, planning and invoicing are each one switch in Settings. Switching something off hides it and keeps everything in it.',
    setupB1: 'Three setups to start from: Simple, Standard and Full.',
    setupB2: 'Choose what each person sees. A contractor can have just the timesheet.',
    setupB3: 'Turn an area back on and it is exactly as you left it.',
    moreTitle: 'When you need more',
    moreBody: 'Everything here is already built. It stays out of sight until you switch it on.',
    moreTasks: 'Tasks',
    moreTasksWhere: 'Standard',
    moreTasksBody: 'Three levels of subtasks, assignees, due dates, comments, a board, and CSV import from your old tool.',
    moreClients: 'Clients',
    moreClientsWhere: 'Standard',
    moreClientsBody: 'A page per client with its projects and hours, retainers, and a link you can share with the client.',
    morePlanning: 'Planning',
    morePlanningWhere: 'Full, Team plan',
    morePlanningBody: 'Capacity after leave, hour budgets that warn at 80%, a timeline, dependencies and goals.',
    moreFinance: 'Invoices',
    moreFinanceWhere: 'Full, Business plan',
    moreFinanceBody: 'Rates per client, project or task, invoices drafted from billable hours, quotes, expenses and payroll.',
    moreReports: 'Reports',
    moreReportsWhere: 'Every setup',
    moreReportsBody: 'Hours by person, project and task over any dates. CSV export on Team and up.',
    moreDesktop: 'Desktop timer',
    moreDesktopWhere: 'Every setup',
    moreDesktopBody: 'For Mac and Windows. Search a task, start and stop without opening a browser.',
    storySign: 'Iordanis Passas, ipassas design, Syros',
    navWork: 'Product',
    navFeatures: 'Features',
    navPricing: 'Pricing',
    navCompare: 'Compare',
    navStudio: 'About',
    cta: 'Start free',
    ctaNoCard: 'Start free, no card needed',
    ctaSecondary: 'See pricing',
    ctaTrial: 'Start 7-day trial',
    contact: 'Talk to us',
    menu: 'Menu',
    closeMenu: 'Close menu',

    heroTitle1: 'Timesheets and leave for small teams.',
    heroBody:
      'Start a timer or fill in the week on a timesheet, book time off, and see who is away. That is all most teams need, so it is all a new workspace shows. Tasks, clients, planning and invoices are there when you switch them on.',



    gridAllLink: 'Open the full feature list',





    pricingTitle: '$2 a seat, or free for three people.',
    pricingBody:
      'Prices are per person, per month. Starter is free for up to three people. A new workspace gets seven days of Team to try, with no card. Paying yearly takes 20% off.',
    billingMonthly: 'Monthly',
    billingYearly: 'Yearly · save 20%',
    perSeatMonth: 'per seat / month',
    billedYearly: 'billed yearly',
    free: 'Free',
    custom: 'Custom',
    bestFor: 'Best for',
    planFree: 'Starter',
    planFreeFor: 'Freelancers and very small teams.',
    planFreeSeats: 'Up to 3 people · 3 projects · 2 clients',
    planFree1: 'Timer, manual entries and the weekly timesheet',
    planFree2: 'Leave requests, approvals and balances',
    planFree3: 'Three-level tasks with tags, checklists and links, as a list or a board',
    planFree4: 'Planner, ⌘K search, shortcuts, bulk edit, trash',
    planTeam: 'Team',
    teamTag: 'Most popular',
    planTeamFor: 'Studios and agencies that bill by the hour.',
    planTeamSeats: 'Up to 25 people · unlimited projects and clients',
    planTeam0: 'Everything in Starter, plus:',
    planTeam1: 'All nine reports over any range, CSV export',
    planTeam2: 'Capacity week, month targets, hour budgets, project health',
    planTeam3: 'Watchers, repeating tasks, relationships, milestones',
    planTeam4: 'CSV import from your current tool, task export',
    planTeam5: 'Due-date reminders, weekly digest, Team page, Today list',
    planTeam6: 'Your logo and colours, email notifications, Google Calendar',
    planTeam7: 'Budget runway, estimate calibration, due dates checked against leave',
    planTeam8: 'Timeline, dependencies, goals, burndown, automations, project briefs, request forms',
    planBusiness: 'Business',
    planBusinessFor: 'Firms with managers and an accountant.',
    planBusinessSeats: 'Up to 100 people',
    planBusiness0: 'Everything in Team, plus:',
    planBusiness1: 'Scheduled weekly and monthly report emails',
    planBusiness2: 'Audit log and a full export of all your data',
    planBusiness3: 'Project templates, bulk leave allowances, public holidays',
    planBusiness4: 'Activity feed on the dashboard and Team page',
    planBusiness5: 'Priority email support',
    planBusiness6: 'Rates, PDF invoices, payroll and margin, and an accountant role',
    planBusiness7: 'Client retainers with rollover, retainer invoices and a client portal',
    planBusiness8: 'Quotes, expenses, profitability forecasts, priced workload, client currencies',
    planEnterprise: 'Enterprise',
    planEnterpriseFor: 'Organisations of more than a hundred people.',
    planEnterpriseSeats: 'From 50 seats · unlimited',
    planEnterprise0: 'Everything in Business, plus:',
    planEnterprise1: 'Invoice billing and a signed agreement',
    planEnterprise2: 'Help getting started and a named contact',
    planEnterprise3: 'We move your data over from your current tool',
    planEnterprise4: 'A say in what gets built next',
    pricingNote: 'Prices in US dollars. The 7-day trial is of the Team plan and needs no card. When it ends, the workspace moves to Starter unless you choose a plan, and nothing is deleted.',

    calcLabel: 'What would your team pay?',
    calcSeats: '{count} people',
    calcUs: 'Trackilero {plan}',
    calcThem: 'The usual tools, {plan}',
    calcPerMonth: 'a month',
    calcSaving: 'You keep {amount} a month',
    calcNote: 'Typical list prices of the leading work-management suites, August 2026, monthly billing: $10 a seat on the mid tier, $19 on the one above.',

    faqLabel: 'Questions',
    q1: 'Is there really a free plan?',
    a1: 'Yes. Starter is free for up to three people, three projects and two clients. It has the timer, the weekly timesheet, leave, three-level tasks and the planner, and it does not expire.',
    q2: 'Can we move from the tool we use now?',
    a2: 'Yes. Export a list as CSV and import it into a project. Subtasks, statuses, priorities, dates, estimates, assignees and tags come across, and you check every row before it is created. On Enterprise we do the move for you.',
    q3: 'How does Trackilero handle rounding?',
    a3: 'It doesn\'t round. Every minute the timer records or you type in is stored and shown exactly. If an invoice needs rounding, apply it to the export and the record stays exact.',
    q4: 'Where is the data, and can we take it out?',
    a4: 'In Google\'s Firebase in the EU (europe-west1, Belgium), encrypted in transit and at rest. On Team, every report downloads as CSV. On Business and Enterprise you can export all of your data in one go.',
    q5: 'Do we have to use all of it?',
    a5: 'No. A workspace runs in one of three setups. Simple is time tracking, a calendar of your week and leave. Standard adds tasks, clients and the Team page. Full adds planning and invoicing. Change it any time in Settings → Features, switch single areas on or off, or choose what each person sees. Hidden areas keep their data, and time tracking is always on. There is no AI and no chat in any of them.',
    q6: 'How is it so cheap?',
    a6:
      'Because it wasn\'t built to be sold. I made it for my own studio, ipassas design, to keep track of my calendar and my work, and used it every day for years before anyone else saw it. There is no sales team and no investor waiting for a return. Nothing is uploaded, since you attach links instead of files, so there is no storage bill. A seat costs about ten cents a month to run on Google\'s servers. The price covers that many times over, and the rest pays for the time I spend improving it.',

    storyTitle: 'I built it to run my own studio.',
    storyBody1:
      'I run ipassas design, a studio that started as just me and grew into a small team. I needed to know where my week had gone, which client the hours belonged to, and whether Thursday was already booked. The tools I tried cost a hundred dollars a month for features I would never open, or they rounded my minutes and lost my subtasks. So I built my own: a calendar to plan the week, a timer that kept exact minutes, and a task list three levels deep, because that is how my work is organised.',
    storyBody2:
      'It ran the studio for years before anyone else used it, and most of what is in it was added after something went wrong. The capacity week came after I promised a date that depended on someone who was on holiday. The budget notice came after a project ran sixty hours over without anyone noticing. The subtask column came after an invoice went out with the same hours counted twice.',
    storyBody3:
      'Other studios kept asking what I was using, so now anyone can sign up. Some told me it had become a lot to take in. So a workspace now starts with time and leave, and you switch on the rest only if you need it. I still plan my own week on it.',


    closeTitle: 'Set it up in ten minutes. Log your first hour today.',
    closeNote: 'Free for up to three people. Every new workspace also gets seven days of Team, with no card. Start with Simple and switch on more later.',
    footer: 'Made by',
    footerBy: 'ipassas design',
    privacy: 'Privacy',
    refunds: 'Refunds',
    cookies: 'Cookies',
    copyright: 'Trackilero 2026. All rights reserved.',
  },

  history: {
    tab: 'History',
    empty: 'Nothing has happened to this task yet. Changes and logged time will show here.',
    system: 'System',
    created: 'created the task',
    status: 'moved it from {before} to {after}',
    completed: 'marked it done',
    reopened: 'reopened it',
    assigneesAdded: 'assigned {names}',
    assigneesRemoved: 'unassigned {names}',
    assigneesChanged: 'changed assignees to {names}',
    nobody: 'nobody',
    dueSet: 'set the due date to {after}',
    dueCleared: 'cleared the due date',
    dueMoved: 'moved the due date from {before} to {after}',
    estimateSet: 'estimated {after}',
    estimateCleared: 'removed the estimate',
    estimateChanged: 'changed the estimate from {before} to {after}',
    priority: 'set priority to {after}',
    title: 'renamed it from "{before}"',
    timeLogged: 'logged {minutes}',
    timeEdited: 'changed an entry from {before} to {after}',
    timeDeleted: 'deleted an entry of {minutes}',
    tagAdded: 'added a tag',
    tagRemoved: 'removed a tag',
    tagsChanged: 'changed the tags',
    showEarlier: 'Show earlier',
    loadingEarlier: 'Loading\u2026',
    showMore: 'Show earlier history',
  },

  views: {
    menu: 'Views',
    options: 'Options for {name}',
    saveCurrent: 'Save current view…',
    update: 'Update "{name}" with current filters',
    mine: 'My views',
    shared: 'Shared views',
    none: 'No saved views yet. Set some filters and save them.',
    dialogTitle: 'Save view',
    dialogHint: 'The filters and layout you have now, under a name you will recognise.',
    name: 'Name',
    namePlaceholder: 'Blocked and urgent',
    nameRequired: 'Give the view a name.',
    shareLabel: 'Share with everyone in the organisation',
    shareHint: 'Only admins can share a view. Anyone can save one for themselves.',
    save: 'Save view',
    setDefault: 'Open by default',
    clearDefault: 'Stop opening by default',
    isDefault: 'Default',
    delete: 'Delete view',
    deleteTitle: 'Delete this view?',
    deleteBody: '"{name}" will be removed. The tasks it shows are untouched.',
    saved: 'View saved',
    updated: 'View updated',
    deleted: 'View deleted',
    applied: 'Showing "{name}"',
    defaultSet: '"{name}" will open by default',
    defaultCleared: 'No default view',
    activeChip: 'View: {name}',
  },

  capacity: {
    title: 'Capacity',
    summary: '{demand} claimed of {available} available',
    overCount: '{count} people over',
    overCount_one: '1 person over',
    everyoneFits: 'Everyone fits',
    person: 'Person',
    available: 'Available',
    planned: 'Planned',
    due: 'Due',
    load: 'Load',
    team: 'Team',
    off: 'Off',
    pendingOff: 'Pending',
    half: '½',
    halfOff: 'half day off',
    halfPending: 'half day pending',
    stateLight: 'Light',
    stateOk: 'Fits',
    stateFull: 'Full',
    stateOver: 'Over by {amount}',
    legendOff: 'Approved leave',
    legendPending: 'Pending leave',
    legendPlanned: 'Planned in the planner',
    value: 'Value',
    cost: 'Cost',
    legendNote:
      'Available is the daily target minus leave. Due is what is left of estimates on tasks due this week that have no planner block.',
    emptyTitle: 'Nobody to plan for yet',
    emptyBody: 'Add people in Settings and their week appears here.',
    targetsTitle: 'This month against targets',
    workingDays: 'Working day {elapsed} of {total}',
    noTargetsBody:
      'Nobody has a monthly target yet. Each person sets one on their profile, or an admin sets it in Settings.',
    withoutTarget: '{count} people without a target',
    withoutTarget_one: '1 person without a target',
    paceMet: 'Target met',
    paceAhead: 'Ahead · heading for {projected}',
    paceOnTrack: 'On track · heading for {projected}',
    paceBehind: 'Behind · heading for {projected}',
    neededPerDay: '{amount} a day to get there',
    offDay: 'Not a working day',
    edit: 'Edit hours and days',
    editOwn: 'Edit my hours and days',
    saved: 'Capacity saved',
    pipelineTitle: 'If the pipeline lands',
    pipelineHint:
      'Open quotes laid over the weeks ahead at the likelihood somebody gave them, beside what accepted work has already promised. Hours, never money.',
    pipelineToggle: 'Show pipeline',
    pipelineBand: 'Hours',
    pipelineAvailable: 'The team can work',
    pipelineCommitted: 'Accepted quotes',
    pipelineWeighted: 'Open quotes, weighted',
    pipelineVerdict: 'Verdict',
    pipelineOver: 'Over by {hours}',
    pipelineOverIfAll: 'Over by {hours} if all land',
    pipelineFits: 'Fits',
    pipelineUndated: '{count} quotes carrying {hours} are not on the grid: they have no start week, or theirs falls outside it.',
    pipelineUndated_one: 'One quote carrying {hours} is not on the grid: it has no start week, or its week falls outside this run.',
    upcomingTitle: 'Upcoming weeks',
    upcomingHint:
      'Available hours and working days for each person, with approved and pending leave already taken out. The manager named as approver is emailed when someone changes theirs.',
    week: 'Week of {date}',
    daysCount: '{count} days',
    daysCount_one: '1 day',
    unavailable: 'Unavailable',
    myWeekTitle: 'Your week',
    myWeekBody: 'Only managers see the whole team here. Your own hours and days are below and you can change them.',
  },

  timesheet: {
    locked: 'That entry is on an issued invoice and cannot be changed here.',
    title: 'Timesheet',
    entries: 'Entry list',
    period: 'Period',
    week: 'Week',
    month: 'Month',
    thisWeek: 'This week',
    thisMonth: 'This month',
    previous: 'Previous period',
    next: 'Next period',
    task: 'Task',
    total: 'Total',
    logged: 'logged',
    emptyTitle: 'No time logged in this period',
    emptyBody:
      'Start a timer or add an entry by hand, and the days you worked will fill in across this grid.',
    truncated:
      'Showing the first 800 entries for this period. Narrow the period to see the rest.',
    view: 'View',
    grid: 'Timesheet',
    addTask: 'Add task',
    addTaskHint: 'Put a task on the sheet so you can enter time against it.',
    cellLabel: 'Time on {task}, {date}',
    badDuration: 'Enter a duration like 90, 1:30 or 2h.',
    manyEntries:
      'This day has {count} entries on this task. Edit them in the entry list so their notes are not lost.',
    manyEntries_one: 'This day has 1 entry on this task.',
    startOn: 'Start a timer on {task}',
    today: 'Today',
    noProject: 'No project',
    against: 'Against a target of {target}',
    overBy: '{duration} over',
    ofTarget: 'of {target}',
  },

  carry: {
    openWeek: 'Bring forward',
    openMonth: 'Bring forward',
    titleWeek: 'Bring last week forward',
    titleMonth: 'Bring last month forward',
    description:
      'Unfinished work from the period before this one: tasks assigned to you, and tasks you logged time against. Carrying one forward moves its due date into the period you are looking at. Nothing is copied, so the time already logged stays where it is.',
    confirm: 'Bring {count} forward',
    confirm_one: 'Bring 1 forward',
    all: 'Select all',
    none: 'Select none',
    count: '{count} tasks',
    count_one: '1 task',
    moved: '{count} tasks brought forward',
    moved_one: '1 task brought forward',
    wasDue: 'was due {date}',
    emptyTitle: 'Nothing left behind',
    emptyBody:
      'Every task assigned to you from the period before this one is either finished or already due in it.',
  },

  leave: {
    remaining: 'Remaining',
    allocated: 'Allocated',
    carriedOver: 'Carried over',
    used: 'Used',
    pending: 'Pending',
    days: '{count} days',
    days_one: '1 day',
    halfDayAm: 'Morning',
    halfDayPm: 'Afternoon',
    fullDay: 'Full day',
    approver: 'Approver',
    awaitingApproval: 'Awaiting approval',
    partlyApproved: 'Partly approved',
    statusPending: 'Pending',
    statusApproved: 'Approved',
    statusRejected: 'Rejected',
    statusCancelled: 'Cancelled',
    whoElseIsOff: 'Who else is off',
    dueDuring: 'Due during this leave',
    dueDuringNone: 'Nothing is due in these days.',
    dueDuringCount: '{count} tasks due while {name} is away',
    pushPast: 'Push to {date}',
    pushed: 'Moved to {date}',
    cost: '{days} working days · pay {pay} · about {lost} of billable work not done',
    approverIs: 'Addressed to {name}',
    noApproverSet: 'No approver set — only an owner or admin can decide this',
    rejectTitle: 'Reject {name}’s leave?',
    rejectBody: '{days} day(s), {from} to {to}. The days go back to their balance straight away.',
    rejectReason: 'Reason',
    rejectReasonHint: 'Sent with the decision, in the app and by email. Optional, but it saves the conversation that follows a bare no.',
    rejectReasonPlaceholder: 'We already have two people off that week.',
    rejectConfirm: 'Reject the request',
    decisionNote: 'Reason given',
    pendingReserves: 'Pending requests reserve days from your balance straight away.',
    cover: 'Who covers',
    coverPick: 'Pick a colleague',
    coverNone: 'Nobody',
    coverHint: 'Optional. On the first day of your leave, your open tasks due before you are back land on their list, and go back to you the day you return.',
    coverWhat: 'On the first day of the leave they are put on the open tasks due before the leave ends, told which, and taken off them again the day after it ends.',
    coverSuggested: '{name} suggests {cover} covers',
    coverSuggestedShort: 'Suggested cover: {name}',
    coverBy: 'Cover: {name}',
    coverStarted: 'Leave approved · {name} is covering {count} tasks from today',
    coverStarted_one: 'Leave approved · {name} is covering 1 task from today',
    coverScheduled: 'Leave approved · {name} covers from the first day',
    coverBanner: 'You are covering for {name} until {date}.',
    coverBannerTasks: '{count} of their tasks are on your list; they go back to {name} the day after.',
    coverBannerTasks_one: '1 of their tasks is on your list; it goes back to {name} the day after.',
  },

  report: {
    subtitleTeam: 'Hours logged by everyone in the workspace',
    subtitleOwn: 'Your own hours',
    exportAll: 'Export all reports',
    exportAllBusy: 'Building the file…',
    exportAllHint: 'Every report for the selected dates, as one CSV with a section per report.',
    exportAllDone: 'All reports exported',
    hoursPerPerson: 'Hours per person',
    hoursPerPersonByProject: 'Hours per person by project',
    hoursPerProject: 'Hours per project',
    hoursPerTask: 'Hours per task and subtask',
    billableSplit: 'Billable versus non-billable',
    tasksCompleted: 'Tasks completed',
    estimateVsActual: 'Estimate versus actual',
    leaveBalances: 'Leave taken and remaining',
    whoIsOff: 'Who is off',
    ownHours: 'Own hours',
    hoursInclSubtasks: 'Hours incl. subtasks',
    rollupWarning:
      'Hours including subtasks already contain every child. Do not add this column to the own-hours column.',
  },

  empty: {
    noTasksTitle: 'No tasks here yet',
    noTasksBody: 'Create the first one, or change the filters to widen the search.',
    noTimeTitle: 'No time logged {period}',
    noTimeBody: 'Start a timer or add an entry manually. Nothing is billed until an entry exists.',
    noLeaveTitle: 'No leave requested this year',
    noLeaveBody: 'Pick the days you want off and send them to your approver.',
    noResultsTitle: 'Nothing matches those filters',
    noResultsBody: 'Try removing one of them.',
    noNotificationsTitle: 'You are all caught up',
    noNotificationsBody: 'Mentions and assignments will land here.',
    planLocked: 'Available on the {plan} plan',
    planLockedBody: 'Ask the platform owner to move this workspace to {plan} to use this.',
  },

  twofactor: {
    title: 'Two-factor authentication',
    on: 'On \u2014 an authenticator app is asked for a code at sign-in.',
    off: 'Off. A password alone signs this account in.',
    enable: 'Turn on two-factor',
    disable: 'Turn it off',
    enrollLede: 'Scan this with your authenticator app \u2014 Google Authenticator, 1Password, Authy, any of them \u2014 then type the six-digit code it shows.',
    qrAlt: 'QR code for your authenticator app',
    manualKey: 'No camera? Enter this key by hand:',
    codeLabel: 'Six-digit code',
    wrongCode: 'That is not the code the app shows right now. Codes change every 30 seconds \u2014 try the current one.',
    challengeLede: 'This account asks for a second step. Open your authenticator app and type the code it shows for Trackilero.',
    enabled: 'Two-factor is on. You will be asked for a code at sign-in.',
    disabled: 'Two-factor is off.',
    reauthLede: 'Changing security settings needs a fresh sign-in first.',
    reauthFailed: 'That did not match. Try again.',
    notEnabled: 'Two-factor is not switched on for this platform yet.',
    confirmDisableTitle: 'Turn off two-factor?',
    confirmDisableBody: 'Your password alone will sign this account in again. Any workspace that requires two-factor will ask you to set it up before you can enter.',
    requireTitle: 'Require two-factor authentication',
    requireHint: 'Everyone in this workspace must have an authenticator app set up. Anyone without one is asked to set it up at their next sign-in, and cannot enter until they do.',
    requiredTitle: 'This workspace requires two-factor',
    requiredLede: '{org} requires an authenticator app on every account. Set yours up to continue \u2014 it takes a minute.',
    gateDone: 'Two-factor is set up. Taking you in\u2026',
    requireOn: 'Two-factor is now required. Anyone without it sets it up at their next sign-in.',
    requireOff: 'Two-factor is no longer required.',
  },
  day: {
    title: 'Your day',
    lede: 'Put together from your calendar, your plan and what you moved. Nothing is saved until you say so.',
    nothing: 'Nothing to add — everything today is already logged.',
    nothingThen: 'Nothing to add — that day is already logged.',
    alreadyLoggedThen: '{hours} logged that day.',
    previousDay: 'The day before',
    nextDay: 'The day after',
    today: 'Today',
    yesterday: 'Yesterday',
    daysAgo: '{count} days ago',
    stripLabel: 'The last fortnight',
    stripConfirmed: '{date} \u2014 confirmed',
    stripUnconfirmed: '{date} \u2014 not confirmed yet',
    confirmedAlready: 'You confirmed this day. Anything logged since is in the timesheet below.',
    billable: 'Billable',
    confirmAll: 'Confirm the day',
    confirmOne: 'Add',
    reject: 'Not this',
    rejected: 'Removed. It will not be offered again.',
    confirmed: '{hours} added to your timesheet.',
    pickTask: 'Which task was this?',
    pickTaskHint: 'Your calendar knows when, not what. Pick a task and this can be added.',
    alreadyLogged: '{hours} logged already today.',
    calendarOff: 'Connect your calendar and meetings appear here too.',
    building: 'Reading your day…',
    total: '{hours} to add',
    links: 'Meetings you have answered',
    linksHint: 'A meeting with this name is logged here without asking. Change one by picking a different task, or forget it and it will ask again.',
    linksCount: '{count} answered',
    linksOne: '1 answered',
    forget: 'Forget',
    forgotten: 'Forgotten. That meeting will ask again.',
    linkChanged: 'Changed. That meeting goes to {task} from now on.',
    linkUses: 'used {count} times',
    linkUsesOne: 'used once',
    forgetTitle: 'Forget “{title}”?',
    forgetNothing: 'It will ask again next time. Nothing already logged changes.',
    forgetSome: '{count} entries were logged this way, {hours} in all. Forgetting the answer leaves them where they are — move them if they went to the wrong task.',
    forgetLocked: '{count} of them are on an issued invoice and cannot be moved. The rest can.',
    forgetOnly: 'Just forget it',
    forgetAndMove: 'Move them to',
    moved: '{count} entries moved to {task}.',
    movedNone: 'Nothing to move.',
  },

  share: {
    button: 'Share with client',
    title: 'Share this project',
    lede: 'A read-only page: hours by month, budget, and what has been done. No sign-in, no rates, no names of who did what. Anyone with the link can open it until it expires or you revoke it.',
    expiry: 'Link expires in',
    days30: '30 days',
    days90: '90 days',
    days365: 'A year',
    create: 'Create link',
    created: 'Link created. Copy it now; it is not shown again.',
    copy: 'Copy link',
    copied: 'Link copied',
    existing: 'Active links',
    none: 'No links yet.',
    opens: '{count} opens',
    expires: 'expires {date}',
    revoke: 'Revoke',
    revoked: 'Link revoked',
    planLocked: 'Client links are on the {plan} plan.',
    clientTitle: 'Share with this client',
    clientLede: 'A portal for the client: their projects with hours this month and retainer left, their invoices with PDF downloads, and quotes awaiting an answer with an Accept button. No account, no names, no rates beyond their own documents.',
    kindProject: 'Project page',
    kindClient: 'Client portal',
    shows: 'This link shows',
    showsLede: 'Anything switched off is not sent to the page at all, not merely hidden on it.',
    showsMoneyLocked: 'Invoices and quotes can only be switched on by an owner, admin or accountant.',
    saveDefault: 'Use these as this client’s default',
    lock: 'Ask for a passphrase',
    lockHint: 'The page will not open without it, so a forwarded link is not a public one.',
    lockForced: 'Required, because this link shows invoices or quotes.',
    sendTo: 'Email the passphrase to',
    sendToHint: 'Sent on its own, without the link. Leave empty to hand it over yourself.',
    passphrase: 'Passphrase',
    passphraseOnce: 'Shown once. It is stored only as a hash, so nobody here can look it up again.',
    emailed: 'Emailed to {email}',
    notEmailed: 'Not emailed. Copy it now and send it yourself.',
    copyPassphrase: 'Copy passphrase',
    passphraseCopied: 'Passphrase copied',
    regenerate: 'New passphrase',
    regenerated: 'New passphrase created. The old one no longer opens the page.',
    locked: 'Passphrase',
    unlocked: 'No passphrase',
    defaultSaved: 'Saved as the default for {name}',
    section: {
      hours: 'Hours',
      hoursHint: 'The headline figure: all time on a project, this month on a client.',
      hoursByMonth: 'Hours by month',
      hoursByMonthHint: 'The last six months as a chart.',
      budget: 'Budget',
      budgetHint: 'The hour budget and how much of it is spent.',
      tasksDone: 'Tasks done',
      tasksDoneHint: 'How many top-level tasks are finished, out of how many.',
      taskList: 'Recently completed',
      taskListHint: 'The titles of the last eight finished tasks.',
      projects: 'Projects',
      projectsHint: 'Their active projects and the hours on each this month.',
      retainer: 'Retainer',
      retainerHint: 'The monthly allowance, what is left of it, and its recent months.',
      invoices: 'Invoices',
      invoicesHint: 'Issued and paid invoices, with their PDFs.',
      quotes: 'Quotes',
      quotesHint: 'Quotes awaiting an answer, with an Accept button.',
    },
  },

  scenario: {
    title: 'What if',
    lede: 'Change something and watch the next twelve weeks move. Nothing here is saved — close it and the workspace is exactly as you left it.',
    open: 'What if…',
    close: 'Close',
    add: 'Add a change',
    empty: 'Add a change and the difference appears beside the real figures.',
    hire: 'Hire somebody',
    hireHint: 'What they cost a month, all in.',
    letGo: 'Let somebody go',
    winQuote: 'Win a quote',
    winQuoteHint: 'An accepted amount, landing on a date.',
    loseClient: 'Lose a client',
    loseClientHint: 'What they already owe still arrives. What has not been earned stops.',
    changeRates: 'Move every rate',
    changeRatesHint: 'A percentage, up or down, on money not yet arrived.',
    amount: 'Amount',
    percent: 'Percent',
    when: 'From',
    remove: 'Remove',
    actual: 'As it is',
    forked: 'What if',
    difference: 'Difference',
    payrollNow: 'Payroll {amount} a month',
    payrollThen: '{amount} a month',
    goesNegative: 'Runs out the week of {week}',
    staysPositive: 'Stays above water for twelve weeks',
    week: 'Week of {date}',
  },

  projection: {
    title: 'Projection',
    lede: 'What each person costs and what their hours earn — last month as it happened, next month as it is lined up — and what the open quotes would add if they came in.',
    month: 'Project',
    failed: 'The projection could not be loaded.',
    retry: 'Try again',
    statPay: 'Paid in {month}',
    statPayHint: '{hours} worked',
    statEarned: 'Earned in {month}',
    statEarnedHint: 'Billable hours at the rates for the work, invoiced or not.',
    statInvoiced: 'Invoiced from {month}',
    statInvoicedHint: '{invoiced} on invoices · {open} not yet billed',
    statNextValue: 'Lined up for {month}',
    statNextValueHint: '{hours} assigned, at the rates for the work',
    statNextPay: 'Pay for {month}',
    statNextPayHint: 'Salaries in full; hourly and daily people on their assigned hours.',
    statNextMargin: 'Margin for {month}',
    statNextMarginHint: 'Lined up, less pay.',
    people: 'People',
    peopleNote: 'Assigned hours are the remaining estimate on open tasks, shared among assignees. Salaries do not move with hours.',
    noPeople: 'Nobody is active in this workspace yet.',
    lastMonth: '{month}, as it happened',
    nextMonth: '{month}, as lined up',
    invoicedHours: 'Invoiced',
    openHours: 'Not billed',
    earned: 'Earned',
    capacity: 'Capacity',
    assigned: 'Assigned',
    value: 'Worth',
    team: 'Team',
    billableShort: '{hours} billable',
    taskCount: '{count} tasks',
    unestimated: '{count} unestimated',
    unassigned: '{hours} of open work worth {amount} is assigned to nobody and is not in the table above.',
    quotes: 'If these quotes come in',
    quotesNote: 'Cost is the hours at the team’s blended rate of {rate} an hour last month.',
    quotesNoRate: 'Set pay rates to see what the hours would cost.',
    noQuotes: 'No open quotes. A draft or sent quote appears here for picking.',
    selectAll: 'Select every quote',
    select: 'Select {title}',
    quote: 'Quote',
    status: 'Status',
    likelihood: 'Likelihood',
    revenue: 'Revenue',
    pickHint: 'Tick the quotes you expect to land and the totals appear here.',
    selected: '{count} selected',
    selectedHint: 'Hours on the quotes’ lines.',
    weighted: '{amount} at each quote’s likelihood',
    costHint: 'At the blended cost rate.',
    room: 'Room left in {month}',
    roomHint: '{free} free before these quotes',
    roomNoTargets: 'Set work targets to see capacity.',
    verdictFits: 'Fits: {hours} of room left after these quotes.',
    verdictOver: 'Over by {hours}: the month cannot hold these quotes on top of what is assigned.',
    verdictNoTargets: 'These quotes add {hours}. Set monthly work targets on people to see whether the team has room.',
  },

  finance: {
    nav: 'Finance',
    title: 'Finance',
    subtitle: 'Rates, invoices and payroll for this workspace.',
    tabOverview: 'Overview',
    tabRates: 'Rates',
    tabInvoices: 'Invoices',
    tabPayroll: 'Payroll',
    tabProjection: 'Projection',
    tabSettings: 'Settings',
    tabExpenses: 'Expenses',
    tabQuotes: 'Quotes',
    mode: 'Priced',
    modeHourly: 'Per hour',
    modeFixed: 'Fixed price',
    modeMonthly: 'Per month',
    modeHint: 'Per hour bills the time. Fixed and per month bill the amount, and the hours become the cost side — what the work took, not what it is worth.',
    modeFixedNote: 'Billed once for this project, whatever the hours come to.',
    modeMonthlyNote: 'Billed once a month for as long as the work runs.',
    effectiveRate: 'Effective rate',
    effectiveRateHint: 'What an hour actually earned. Under a fixed price it rises as the work gets faster.',
    rateColumn: 'Earning',
    projectPriced: 'Fixed price on this project \u2014 these hours are everything in it.',
    breakEven: 'Break-even rate',
    breakEvenHint: 'What an hour has to earn for this workspace to cover its costs. Leave empty and none of the margin warnings appear.',
    marginTriage: 'Stop timers on jobs that have stopped paying',
    marginTriageHint: 'On a fixed-price job whose effective rate has reached break-even, starting a timer needs a manager. Off by default: blocking somebody’s timer is the most intrusive thing this product does.',
    triageHealthy: 'Earning {rate} an hour',
    triageWarning: '{hours} of profitable time left',
    triageOver: 'Past break-even — earning {rate} an hour',
    triageBudget: '{hours} of {budget} used',
    triageUnit: 'Priced on \u201c{task}\u201d. These hours are everything under it, this task included.',
    triageOverride: 'Start it anyway',
    triageOverrideNote: 'Recorded in the audit log, with the figures at the time.',
    scopeNotice: 'Draft a scope notice',
    scopeNoticeHint: 'A quote for the work that has gone past the agreed price, and what finishing is expected to take. A draft — nothing is sent.',
    scopeNoticeDone: 'Scope notice drafted: {hours} beyond the agreed price.',
    scopeNoticeOpen: 'Open it',
    contracts: 'Contracts',
    contractsLede: 'A retainer with a term. Hours are credited on the first of every month it runs, and stop when it ends — without anybody remembering to stop them.',
    contractNew: 'New contract',
    contractName: 'Name',
    contractNamePlaceholder: '2026 retainer',
    contractStarts: 'Starts',
    contractEnds: 'Ends',
    contractRolling: 'Rolling — no end date',
    contractRenewal: 'At the end',
    renewalAuto: 'Renews automatically',
    renewalManual: 'Ends unless renewed',
    renewalNone: 'Ends',
    contractNotice: 'Notice period',
    contractNoticeHint: 'How many days before the end the decision is due. You are told once, on that day.',
    contractHours: 'Hours a month',
    contractAmount: 'Price a month',
    contractUplift: 'Rise on renewal',
    contractUpliftHint: 'A percentage applied to the price when it renews automatically. The rise nobody remembers to ask for.',
    contractRollover: 'Unused hours',
    rolloverExpire: 'Expire',
    rolloverFull: 'Carry over',
    rolloverCapped: 'Carry over, up to',
    rolloverHint: 'Carrying over without a cap is how a workspace ends up owing hours it was paid for a year ago.',
    contractStatus: 'Status',
    contractDraft: 'Draft',
    contractActive: 'Active',
    contractIsEnded: 'Ended',
    contractEnd: 'End it now',
    contractEndTitle: 'End {name}?',
    contractEndBody: 'No further hours are credited and the monthly price stops. Everything already credited, drawn and invoiced stays exactly as it is.',
    contractSaved: 'Contract saved',
    contractEnded: 'Contract ended',
    contractTerm: '{from} to {to}',
    contractRollingTerm: 'From {from}, rolling',
    bank: 'Hour bank',
    bankBalance: '{hours} left',
    bankEmpty: 'Nothing credited yet. The first month is credited on its first day.',
    bankLedger: 'Movements',
    bankSystem: 'Automatic',
    adoptTitle: 'This client has a retainer, but no contract',
    adoptBody: 'A contract does the same thing with a term on it: hours are credited every month it runs, unused hours follow a rule you can see, and it stops by itself when it ends. Adopting creates one from what is already set — {hours} a month — starting this month, so nothing is backdated.',
    adoptRollover: 'Unused hours will carry over up to one month, which is what the current setting does.',
    adoptExpire: 'Unused hours will expire, which is what the current setting does.',
    adoptClear: 'Turn the old retainer field off as well',
    adoptClearHint: 'Leave it on for a month if you would rather watch both run first. They do not clash: the field warns and drafts invoices, the contract credits hours.',
    adopt: 'Adopt as a contract',
    adopted: 'Contract created from the retainer, starting {month}.',
    contractsEmpty: 'No contract yet. The retainer field on this client still works; a contract is what makes it stop by itself.',
    month: 'Month',
    locked: 'Finance is on the {plan} plan.',
    lockedBody: 'Rates, invoices with PDF, and payroll against what each person generates.',
    noAccess: 'Only an owner, admin or accountant can see money.',

    // Overview
    invoicedThisMonth: 'Invoiced this month',
    invoiceCount: '{count} invoices',
    generated: 'Generated',
    generatedHint: 'Billable hours at the resolved rates',
    payroll: 'Payroll',
    payrollHint: 'What the team costs this month',
    margin: 'Margin',
    marginHint: 'Generated minus payroll',
    byProject: 'By project',
    project: 'Project',
    client: 'Client',
    hours: 'Hours',
    billableHours: 'Billable',
    value: 'Value',
    cost: 'Cost',
    marginCol: 'Margin',
    noWork: 'No time was logged in this month.',
    rateNote: 'Value uses project, client and workspace rates. Task rates apply on invoices.',

    // Rates
    ratesTitle: 'Hourly rates',
    ratesLede: 'The most specific rate wins: a task’s rate over its project’s, a project’s over its client’s, and the workspace default last.',
    workspaceRate: 'Workspace default',
    workspaceRateHint: 'Used wherever nothing more specific is set.',
    perHour: '/h',
    clientRates: 'Clients',
    projectRates: 'Projects',
    taskRates: 'Task overrides',
    taskRatesHint: 'Set from a task’s page. Listed here so nothing is hidden.',
    noTaskRates: 'No task has its own rate.',
    inherited: 'inherits',
    clearRate: 'Clear',
    rateSaved: 'Rate saved',
    rateCleared: 'Rate cleared',
    badAmount: 'Enter an amount like 85 or 85.50.',
    taskRate: 'Bill rate',
    taskRateHint: 'Overrides the project and client rate for time on this task.',

    // Invoices
    newInvoice: 'New invoice',
    invoicesEmpty: 'No invoices yet. Draft one from a client’s billable hours.',
    draftTitle: 'Draft an invoice',
    draftLede: 'Billable time for the client in the period, one line per task at the resolved rate. Time already on an issued invoice is left out.',
    allProjects: 'Every project',
    from: 'From',
    to: 'To',
    draft: 'Draft',
    drafted: 'Drafted {lines} lines from {hours} hours',
    draftedSkipped: '{count} entries were already invoiced',
    draftedNoRate: '{count} entries had no rate and are on the draft at zero',
    draftEmpty: 'Nothing billable in that period that is not already invoiced.',
    preflightCheck: 'Check first',
    preflightChecking: 'Checking',
    preflightReady: 'Nothing to fix. Every entry has a note, a rate, a sane length and no twin.',
    preflightQuestions: '{count} things a client might ask about',
    preflightQuestions_one: '1 thing a client might ask about',
    preflightScope: '{entries} entries, {hours} would reach the invoice',
    preflightInvoiced: '{count} already invoiced, left out',
    preflightExpenses: '{count} expenses ({amount}) will be re-billed',
    preflightCount: '{count} · {hours}',
    preflightMore: '… and {count} more',
    preflight_blank_note: 'Entries with no note',
    preflight_long_entry: 'Entries longer than a working day',
    preflight_auto_stopped: 'Timers that were stopped at twelve hours',
    preflight_no_rate: 'Entries no rate reaches — they land on the draft at zero',
    preflight_duplicate: 'The same person, task, day and length twice',
    preflight_already_invoiced: 'Already on an invoice',
    preflightAsk: 'Ask {count} people for notes',
    preflightAsk_one: 'Ask for the notes',
    preflightAsked: 'Asked',
    preflightAskedToast: 'Asked {count} people to add their notes',
    preflightAskedToast_one: 'Asked 1 person to add their notes',
    number: 'Number',
    status: 'Status',
    period: 'Period',
    issued: 'Issued',
    due: 'Due',
    total: 'Total',
    statusDraft: 'Draft',
    statusIssued: 'Issued',
    statusPaid: 'Paid',
    statusVoid: 'Void',
    statusOverdue: 'Overdue',
    open: 'Open',

    // Editor
    backToInvoices: 'Invoices',
    editorDraftHint: 'Edit anything below. Issuing assigns the number and locks the lines.',
    lines: 'Lines',
    description: 'Description',
    rate: 'Rate',
    amount: 'Amount',
    addLine: 'Add a line',
    removeLine: 'Remove line',
    subtotal: 'Subtotal',
    tax: 'Tax',
    taxPercent: 'Tax %',
    notes: 'Notes',
    notesPlaceholder: 'Payment instructions, a thank-you, a reference.',
    billTo: 'Bill to',
    issuer: 'From',
    partyName: 'Name',
    partyAddress: 'Address',
    partyEmail: 'Email',
    partyVat: 'VAT / tax id',
    dueDate: 'Due date',
    save: 'Save draft',
    saved: 'Draft saved',
    issue: 'Issue invoice',
    issueConfirm: 'Issue this invoice?',
    issueConfirmBody: 'It gets the next number in sequence and its lines are locked. You can still mark it paid or void.',
    issuedToast: 'Issued as {number}',
    downloadPdf: 'Download PDF',
    send: 'Email to client',
    sendAgain: 'Email again',
    sendConfirm: 'Email this invoice to {email}?',
    sendConfirmBody: 'The PDF goes as an attachment, from your workspace’s address, with the invoice notes as the message.',
    sent: 'Sent to {email}',
    outstanding: 'Outstanding',
    outstandingHint: 'Issued, not yet paid',
    overdue: 'Overdue',
    overdueHint: 'Past their due date',
    markPaid: 'Mark paid',
    markUnpaid: 'Mark unpaid',
    voidInvoice: 'Void',
    voidConfirm: 'Void this invoice?',
    voidConfirmBody: 'Its number stays used, and its hours become billable again on the next draft.',
    deleteDraft: 'Delete draft',
    deleteConfirm: 'Delete this draft?',
    deleteConfirmBody: 'Nothing has been issued; the hours stay billable.',
    recomputeAmount: 'Recalculate from hours × rate',
    hoursCol: 'Hours',

    // Payroll
    payrollTitle: 'Payroll',
    payrollLede: 'Each person on their own basis, against what their billable hours generated at the resolved rates. A ledger, not a payment.',
    invoice: 'Invoice',
    ledgerItem: 'Ledger item',
    ledgerItemHint: 'The product or service exported lines are booked against. It has to be one QuickBooks already holds, or it refuses the row.',
    ledgerTitle: 'Send to QuickBooks',
    ledgerHint: 'Two files in QuickBooks\u2019 own import shape: the customers, then the invoices, every line booked to \u201c{item}\u201d. Import the customers first.',
    ledgerFrom: 'Issued from',
    ledgerTo: 'Issued to',
    ledgerExport: 'Export for QuickBooks',
    ledgerIncludeSent: 'Include invoices already exported',
    ledgerOrder: 'Only issued and paid invoices cross over. Drafts and voided invoices are not ledger entries.',
    ledgerExported: '{invoices} invoices and {customers} customers exported. Import the customers file first.',
    ledgerExported_one: 'One invoice and {customers} customers exported. Import the customers file first.',
    ledgerNothing: 'Nothing new in that range. Turn on \u201cinclude invoices already exported\u201d to send them again.',
    fxTitle: 'Currency exposure',
    fxHint: 'Open invoices in a client\u2019s currency, at the rate they were booked at against the rate on the client record today.',
    fxAmount: 'Invoiced',
    fxLocked: 'Booked at',
    fxToday: 'At today\u2019s rate',
    fxMove: 'Unrealised',
    fxTotal: 'Open exposure',
    pipelineTitle: 'On the capacity grid',
    pipelineLede: 'This quote carries {hours} hours. Say when they would start and how likely the quote is, and the weeks ahead will show what happens if it lands.',
    pipelineNoHours: 'This quote carries no hours on its lines, so there is nothing to place on the capacity grid. Add hours to a line first.',
    pipelineStart: 'Would start',
    pipelineWeeks: 'Weeks',
    pipelineProbability: 'Likelihood %',
    pipelineAccepted: 'Accepted, so counted in full.',
    pipelineSave: 'Put on the grid',
    pipelineClear: 'Take off the grid',
    pipelineOffGrid: 'Without a start week it stays off the grid.',
    boughtIn: 'Bought in',
    boughtInSummary: '{hours} from {count} contractors this month. They cost {cost}, the clients were billed {value}, and the difference \u2014 {margin}, a {markup} markup \u2014 is margin on work the studio did not do itself.',
    boughtInSummary_one: '{hours} from one contractor this month. They cost {cost}, the client was billed {value}, and the difference \u2014 {margin}, a {markup} markup \u2014 is margin on work the studio did not do itself.',
    person: 'Person',
    basis: 'Basis',
    basisHourly: 'Hourly',
    basisDaily: 'Daily',
    basisFixed: 'Fixed monthly',
    basisNone: 'Not set',
    pay: 'Pay',
    daysWorked: 'Days',
    payTotal: 'Total payroll',
    setPay: 'Set pay',
    editPay: 'Edit',
    payFor: 'Pay for {name}',
    payAmount: 'Amount',
    payAmountHint: 'Per hour, per worked day, or per month, by basis.',
    removePay: 'Remove',
    paySaved: 'Pay saved',

    // Settings
    settingsTitle: 'Invoice settings',
    // Expenses
    expensesTitle: 'Expenses',
    expensesLede: 'Costs on a project — stock, software, travel — logged as they happen. Billable ones are re-billed on the client’s next invoice, once.',
    addExpense: 'Add expense',
    expenseDate: 'Date',
    expenseDescription: 'What it was',
    expenseAmount: 'Amount',
    expenseBillable: 'Re-bill to the client',
    expenseProject: 'Project',
    expenseBy: 'Logged by',
    expenseInvoiced: 'Invoiced',
    expenseUnbilled: 'Not yet invoiced',
    expenseInternal: 'Internal',
    expensesEmpty: 'No expenses yet.',
    expenseSaved: 'Expense saved',
    expenseDeleted: 'Expense deleted',
    expenseDelete: 'Delete this expense?',
    expenseDeleteBody: 'It comes off the project’s cost. If it was on an invoice, that invoice keeps its line.',
    expenseTotal: 'Total',
    expenseCostNote: 'Expenses count as project cost in the margin overview.',
    draftedExpenses: '{count} expenses added',

    // Quotes
    quotesTitle: 'Quotes',
    quotesLede: 'What the work would cost, before it is done. Start from a project’s estimates or from nothing, send it, and turn it into an invoice when they say yes.',
    newQuote: 'New quote',
    quoteTitleField: 'Title',
    quoteTitlePlaceholder: 'Website redesign, phase one',
    quoteFromEstimates: 'Start from the project’s task estimates',
    quoteFromEstimatesHint: 'One line per top-level task: its estimate (including subtasks) at the resolved rate.',
    quoteCreate: 'Create quote',
    quoteCreated: 'Quote drafted with {lines} lines',
    quoteValid: 'Valid until',
    quoteSend: 'Mark as sent',
    quoteEmail: 'Email to client',
    quoteAccept: 'Accepted',
    quoteDecline: 'Declined',
    quoteConvert: 'Turn into an invoice',
    quoteConvertConfirm: 'Create an invoice from this quote?',
    quoteConvertConfirmBody: 'A draft invoice with the same lines. The quote is marked converted and keeps pointing at it.',
    quoteConverted: 'Invoice drafted from the quote',
    quoteIssue: 'Number and send',
    quoteIssued: 'Issued as {number}',
    quoteStatusDraft: 'Draft',
    quoteStatusSent: 'Sent',
    quoteStatusAccepted: 'Accepted',
    quoteStatusDeclined: 'Declined',
    quoteStatusConverted: 'Converted',
    quotesEmpty: 'No quotes yet.',
    quoteOpenInvoice: 'Open the invoice',
    quantity: 'Qty (hours)',
    quotePrefix: 'Quote number prefix',
    quoteValidDays: 'Quotes valid for (days)',
    paymentReminders: 'Remind clients when an invoice is overdue',
    paymentRemindersHint: 'An email with the PDF the day after the due date, then weekly, until it is marked paid.',

    retainerBilling: 'Retainer billing',
    retainerAmount: 'Monthly amount',
    retainerAmountHint: 'For the {hours}h allowance.',
    overageRate: 'Overage rate /h',
    overageRateHint: 'Empty: the client’s resolved rate.',
    autoDraft: 'Draft the retainer invoice on the 1st of each month',
    clientCurrency: 'Client currency',
    clientCurrencyHint: 'Rates for this client are entered in it; invoices and quotes print in it. Empty: the workspace currency.',
    fxRate: 'Rate to {currency}',
    fxRateHint: 'One unit of the client’s currency in {currency}, for the overview and payroll. 1 when they match.',
    weeklySummary: 'Email this client a weekly summary',
    weeklySummaryHint: 'Every Monday: last week’s hours by project, what was completed, retainer left. No names, no rates.',

    cashflow: 'Cash flow, next twelve weeks',
    cashflowLede: 'Invoices by due date, retainers on the 1st plus terms, accepted quotes thirty days out — against payroll and the run-rate of expenses.',
    cashflowOverdue: 'Overdue and unpaid: {amount}',
    cashIn: 'Expected in',
    cashOut: 'Payroll and expenses',
    cashCumulative: 'Running total',
    cashLow: 'Lowest point {amount}, week of {week}',
    rateReview: 'Rate review, last six months',
    rateReviewLede: 'The rate on the invoices, and what every tracked hour for the client really earned once unbilled time is counted — against what an hour costs you.',
    nominal: 'Nominal',
    effective: 'On invoices',
    earnedPerHour: 'Earned per tracked hour',
    trend: 'By month',
    underCost: 'below cost ({cost}/h)',
    approvalThreshold: 'Approval above',
    approvalThresholdHint: 'Invoices over this amount need an owner or admin to approve before an accountant can issue them. Empty: off.',
    approve: 'Approve',
    approvedBy: 'Approved by {name}.',
    approvalNeeded: 'Over {amount}: an owner or admin must approve before it can be issued.',
    paymentUrl: 'Pay online link',
    paymentUrlHint: 'Printed on every invoice and the client portal. A payment link or a bank page; nothing is processed here.',
    exportTitle: 'Export the books',
    exportHint: 'Invoices, lines, quotes, expenses and pay rates for this year, as CSV.',
    export: 'Export CSVs',
    exported: '{count} files downloaded',

    lookTitle: 'How documents look',
    template: 'Template',
    tplClassic: 'Classic',
    tplClassicHint: 'Quiet rules, parties side by side.',
    tplModern: 'Modern',
    tplModernHint: 'An accent band across the top and a boxed total.',
    tplMinimal: 'Minimal',
    tplMinimalHint: 'Wide margins, one accent rule, nothing else.',
    tplBold: 'Bold',
    tplBoldHint: 'Black header, heavy rules, a big total.',
    tplLedger: 'Ledger',
    tplLedgerHint: 'Serif type and striped rows, like a bookkeeper’s page.',
    tplCompact: 'Compact',
    tplCompactHint: 'Dense: more lines on a page.',
    accent: 'Accent colour',
    accentHint: 'Used by the templates that colour anything.',
    docLogo: 'Logo on documents',
    docLogoHint: 'PNG or JPEG. Leave empty to print the workspace logo.',
    resetYearly: 'Restart numbering every year',
    resetYearlyHint: 'Put {YYYY} in the prefix to print the year, e.g. “{YYYY}-” gives 2026-0001.',
    builderTitle: 'Document builder',
    builderLede: 'Set the type, the margins and where things sit, on top of the template. Add a line under the parties, terms under the total, a signature at the foot. The preview is what the PDF prints.',
    layoutFont: 'Type family',
    layoutTemplateOwn: 'The template’s own',
    fontSans: 'Sans (Helvetica)',
    fontSerif: 'Serif (Times)',
    fontMono: 'Mono (Courier)',
    layoutBaseSize: 'Text size',
    layoutTitleSize: 'Heading size',
    layoutMargin: 'Page margin',
    layoutPoints: '{n} pt',
    layoutHeadingSide: 'Heading',
    headingLeft: 'On the left',
    headingRight: 'On the right',
    layoutLogo: 'Print the logo',
    layoutParties: 'From and Bill to',
    partiesSide: 'Side by side',
    partiesStack: 'One above the other',
    layoutColumns: 'Show on the table',
    colPeriod: 'Period',
    colHours: 'Hours',
    colRate: 'Rate',
    layoutBlocks: 'Blocks',
    layoutBlocksHint: 'Up to {max}. Plain text, set in the document’s type.',
    addTextBlock: 'Add a text block',
    addSignature: 'Add a signature',
    blockText: 'Text block',
    blockSignature: 'Signature',
    blockLabel: 'Label',
    blockLabelHint: 'Small caps above the text. Optional.',
    blockBody: 'Text',
    blockSignatureBody: 'Name and title under the line',
    blockPlacement: 'Where',
    placeAfterParties: 'Under the parties',
    placeAfterTotals: 'Under the total',
    placeFooter: 'At the foot',
    blockAlign: 'Align',
    alignLeft: 'Left',
    alignCenter: 'Centre',
    alignRight: 'Right',
    blockSize: 'Size',
    sizeSmall: 'Small',
    sizeBase: 'Same as the text',
    sizeLarge: 'Large',
    moveUp: 'Move up',
    moveDown: 'Move down',
    removeBlock: 'Remove',
    layoutPreview: 'Preview',
    layoutReset: 'Back to the template as it is',
    templateForThis: 'Template for this invoice',
    templateDefault: 'Workspace default',
    currency: 'Currency',
    currencyHint: 'ISO code, e.g. EUR, USD, GBP. One for the workspace.',
    prefix: 'Invoice number prefix',
    nextNumber: 'Next number',
    defaultTax: 'Default tax %',
    terms: 'Payment terms (days)',
    issuerDetails: 'Your details on every invoice',
    footer: 'Invoice footer',
    footerHint: 'Bank details, terms, a registration number.',
    settingsSaved: 'Invoice settings saved',
    billingDetails: 'Billing details',
    billingHint: 'Printed as “Bill to” on this client’s invoices.',
  },

  goals: {
    nav: 'Goals',
    title: 'Goals',
    subtitle: 'Targets on what the product already measures. Progress is computed, never typed.',
    new: 'New goal',
    empty: 'No goals yet. Set one on hours, billable hours, tasks done, utilisation or invoiced revenue.',
    titleField: 'Title',
    titlePlaceholder: 'Bill 120h to Acme this quarter',
    kind: 'Measure',
    kindHours: 'Hours tracked',
    kindBillable_hours: 'Billable hours',
    kindTasks_done: 'Tasks completed',
    kindUtilisation: 'Utilisation %',
    kindInvoiced: 'Revenue invoiced',
    target: 'Target',
    targetHint: 'Hours as 120h, a count, a percentage, or an amount.',
    scope: 'Scope',
    scopeAll: 'Whole workspace',
    window: 'Window',
    from: 'From',
    to: 'To',
    create: 'Create goal',
    created: 'Goal created',
    archive: 'Archive',
    archived: 'Goal archived',
    onTrack: 'On track',
    behind: 'Behind',
    ahead: 'Ahead',
    met: 'Met',
    notStarted: 'Not started',
    ended: 'Ended',
    expected: 'expected {percent}% by today',
    progress: '{current} of {target}',
    badTarget: 'Enter a target above zero.',
  },

  burndown: {
    title: 'Burndown',
    hint: 'Remaining estimate, last 30 days',
    estimated: 'As estimated',
    realistic: 'At your pace ×{factor}',
    remaining: '{amount} remaining',
    over: 'Over estimate by {amount}',
    noEstimates: 'Add estimates to tasks to see a burndown.',
  },

  automations: {
    title: 'Automations',
    hint: 'Fixed behaviours, switched on per project.',
    atRiskAt80: 'Set health to at-risk at 80% of the budget',
    flagParent: 'Flag a task when all its subtasks are done (never completes it)',
    notifyWatchers: 'Tell watchers when a task is overdue',
    childrenDone: 'All subtasks done',
  },

  review: {
    title: 'My week',
    subtitle: '{from} to {to}. Yours alone.',
    hours: 'Hours',
    ofTarget: '{percent}% of {target}',
    noTarget: 'No weekly target set',
    billable: 'Billable share',
    billableTarget: 'target {percent}%',
    closed: 'Tasks closed',
    weekFactor: 'estimates held at ×{factor} this week',
    overallFactor: 'your usual factor is ×{factor}',
    gaps: 'Untracked gaps',
    gapsSub: '{minutes} you could still log from the Time page',
    noGaps: 'Every timer stretch accounted for',
    closedList: 'Closed this week',
    closedNone: 'Nothing closed yet this week.',
    private: 'This page is built from your own entries and tasks. Nobody else sees it.',
  },

  timeline: {
    unscheduled: 'No date ({count})',
    scheduled: 'Scheduled for {date}',
    trayHint: 'Drag onto a day to schedule',
    empty: 'Give tasks due dates to see them on the timeline.',
    today: 'Today',
    runway: 'Budget runs out {date}',
  },

  deps: {
    stillWaitingTitle: 'Still waiting on {count}',
    stillWaitingBody: 'This task is blocked by work that is not done yet. Marking it done is allowed \u2014 nothing enforces the order \u2014 but the tasks it waits on stay open.',
    stillWaitingConfirm: 'Mark done anyway',
    shifted: 'Pushed {count} dependent tasks by {days} days',
    shiftedCost: 'Pushed {count} dependent tasks by {days} days · about {cost} of remaining work moves with them',
  },

  brief: {
    title: 'Brief',
    write: 'Write the brief',
    placeholder: 'What this project is, what done looks like, where the files are. URLs become links.',
    empty: 'No brief yet. One page on what this project is and what done looks like.',
    saved: 'Brief saved',
    updatedBy: 'by {name}',
    history: 'History',
    noHistory: 'No earlier versions.',
    restore: 'Restore',
  },

  intake: {
    menu: 'Request form',
    title: 'Request form for this project',
    lede: 'A public page where anyone with the link files a request as a task on this project. They get a link back to follow its status and due date, and nothing else.',
    formTitle: 'Form title',
    formTitlePlaceholder: 'Requests for the website',
    intro: 'Intro text',
    introPlaceholder: 'Tell us what you need and we will schedule it.',
    create: 'Create form',
    existing: 'Forms',
    submissions: '{count} requests',
    open: 'Open',
    closed: 'Closed',
  },

  billing: {
    tab: 'Billing',
    tierTitle: 'This workspace is on {plan}',
    trial: 'trial',
    youAreOn: 'This workspace is on',
    seatsLine: '{used} of {limit} seats in use',
    trialEnds: 'trial ends {date}',
    trialLede: 'Everything is unlocked for the week, whatever you pick \u2014 no card until the trial ends. Choosing now just means the checkout is ready when it does.',
    choosePlan: 'This is my plan',
    chosenPlan: 'Your pick',
    chosenNote: 'No card needed until {date}.',
    renews: 'renews {date}',
    endsOn: 'ends {date}',
    pastDue: 'payment overdue — update the card',
    credit: 'Credit on account',
    creditHint: 'Prepaid credit is applied to your next invoices automatically, so adding a person never needs a separate payment.',
    creditDone: 'Credit added. It will show here once Stripe confirms the payment.',
    checkoutDone: 'Subscription started. The plan updates here once Stripe confirms.',
    portal: 'Cards and invoices',
    cancelHint: 'Ends at the close of the paid period; the workspace then moves to Starter. Nothing is deleted.',
    portalHint: 'Opens Stripe’s portal: change the card, download invoices.',
    plans: 'Plans',
    monthly: 'Monthly',
    yearly: 'Yearly, 20% off',
    perSeat: '/ seat / {interval}',
    month: 'month',
    year: 'year',
    seatsUpTo: 'Up to {count} people',
    seatsUnlimited: 'Unlimited people',
    yourTotal: '{amount} for your team today',
    current: 'current',
    onStarter: 'Free, up to three people.',
    starterNote: 'Cancelling a subscription returns here at the end of the period.',
    downgradeToFree: 'Downgrade to Starter',
    subscribe: 'Subscribe',
    upgrade: 'Upgrade',
    downgrade: 'Downgrade',
    cancel: 'Cancel subscription',
    resume: 'Resume subscription',
    changed: 'Plan changed to {plan}',
    cancelled: 'Subscription will end at the period’s close',
    resumed: 'Subscription resumed',
    cancelConfirm: 'Cancel the subscription?',
    cancelConfirmBody: 'The plan stays until {date}, then the workspace moves to Starter: three seats, no gated features. Nothing is deleted.',
    changeConfirm: 'Change to {plan}?',
    changeConfirmBody: 'Prorated from today on the next invoice. Seats follow your active people automatically.',
    confirmChange: 'Change plan',
    subscribeTitle: 'Start {plan}',
    creditTitle: 'Add credit',
    orderSeats: '{count} seats on {plan}',
    orderInterval: 'Billed',
    orderCredit: 'Prepaid credit',
    payToday: 'Due today',
    payNow: 'Pay {amount}',
    paying: 'Paying…',
    paymentNotDone: 'The payment did not go through. Nothing was charged; try again or use another method.',
    securedByStripe: 'Card details go to Stripe, never to us.',
    sandboxNotice: 'Sandbox: no real money moves. Use card 4242 4242 4242 4242 with any future date and CVC.',
    agree: 'By paying you accept the {refunds} and the {privacy}.',
    refundsLink: 'refund and billing terms',
    privacyLink: 'privacy policy',
    purchases: 'Purchases',
    purchasesHint: 'Every invoice and credit payment on this workspace, newest first. Links open the invoice or receipt at Stripe.',
    purchasesEmpty: 'Nothing has been bought yet.',
    purchaseSubscription: 'Subscription',
    purchaseCredit: 'Credit',
    purchaseStatus_paid: 'Paid',
    purchaseStatus_open: 'Awaiting payment',
    purchaseStatus_void: 'Void',
    purchaseStatus_uncollectible: 'Unpaid',
    purchaseStatus_refunded: 'Refunded',
    purchaseStatus_draft: 'Draft',
    viewInvoice: 'Invoice',
    viewReceipt: 'Receipt',
    seatsNote: 'Seats are counted from active people and updated on the subscription as they come and go; buy more ahead at checkout or with "Change seats" so a large team pays once. Enterprise starts at {min} seats.',
    seats: 'Seats',
    continueToPayment: 'Continue to payment',
    changeSeats: 'Change seats',
    saveSeats: 'Save seats',
    seatsSet: 'Subscription set to {count} seats',
    seatsPickHint: 'At least {min}, your active people; up to {max} on this plan.',
    seatsBought: '{bought} seats on the subscription · {used} in use',
    seatsBoughtHint: 'Adding a person beyond the seats bought raises the count automatically, prorated.',
    promoLabel: 'Promotion code',
    promoPlaceholder: 'Have a code?',
    orderDiscount: '{code} · {percent}% off the first invoice',
  },

  forecast: {
    title: 'Profitability',
    hint: 'At the project’s bill rate; cost from pay bases',
    quoted: 'Quoted',
    noQuote: 'No accepted quote',
    earned: 'Earned so far',
    earnedSub: '{hours} at {rate}/h',
    cost: 'Cost so far',
    costSub: 'margin {margin} · expenses {expenses}',
    atCompletion: 'At completion',
    atCompletionSub: 'cost {cost} · margin {margin} · {remaining} left',
    overQuote: 'At this pace the work exceeds the quote by {over}.',
    scopeCreep: '{count} items added since the quote was accepted, at every depth: {hours}, about {amount} at the bill rate.',
    scopeCreep_one: 'One item added since the quote was accepted: {hours}, about {amount} at the bill rate.',
    boughtIn: '{hours} bought in from contractors: {cost} out, {value} billed, {margin} margin.',
  },

  modules: {
    tab: 'Features',
    title: 'What your team sees',
    subtitle:
      'Turn on only what your team uses. When you switch an area off, it disappears for everyone, but nothing in it is deleted. Switch it back on and everything is where you left it.',
    setupLabel: 'Setup',
    mode_simple: 'Simple',
    mode_simpleBody: 'Time tracking, a calendar of the week, and leave. Enough for most teams.',
    mode_standard: 'Standard',
    mode_standardBody: 'Simple, plus tasks, clients and the Team page.',
    mode_full: 'Full',
    mode_fullBody: 'Everything, including planning and finance.',
    mode_custom: 'Custom',
    customHint: 'Your own mix of areas. Pick a setup to start again from one of the three.',
    inUse: 'In use',
    use: 'Use {mode}',
    includes: 'Includes',
    areasLabel: 'Areas',
    alwaysOn: 'Always on',
    area_time: 'Time tracking',
    area_timeBody: 'The timer, the weekly timesheet, week review and the hours reports.',
    area_calendar: 'Calendar',
    area_calendarBody: 'The planner: your day, week and month, with Google Calendar alongside.',
    area_leave: 'Leave',
    area_leaveBody: 'Leave requests, approvals, balances, public holidays and the team leave calendar.',
    area_tasks: 'Tasks',
    area_tasksBody: 'My tasks, subtasks, assignees, due dates, comments, the board and task import.',
    area_clients: 'Clients',
    area_clientsBody: 'Client pages, retainers, and links you can share with a client.',
    area_team: 'Team',
    area_teamBody: 'The Team page, who is in today, and the activity feed.',
    area_planning: 'Planning',
    area_planningBody: 'Capacity, hour budgets, the timeline, dependencies, milestones and goals.',
    area_finance: 'Finance',
    area_financeBody: 'Rates, invoices, quotes, expenses and payroll.',
    projectsNote:
      'Projects stay visible to managers in every setup, because time is logged against a project\'s tasks.',
    perPersonNote: 'To narrow it down for one person, use the "Sees" column in People.',
    needsPlan: 'Needs {plan}',
    switchedOn: '{area} switched on',
    switchedOff: '{area} switched off',
    modeSet: 'Switched to {mode}',
    undo: 'Undo',

    access: 'Sees',
    accessLabel: 'What they see',
    access_everything: 'Everything',
    access_timeLeave: 'Time and leave',
    access_timeOnly: 'Time only',
    access_custom: 'Custom',
    accessHint:
      'Everything means every area the workspace has switched on, including ones you switch on later. This changes what they see, not what their role allows.',
    accessSet: '{name} now sees: {access}',
    accessOwner: 'The owner always sees everything.',
    accessNarrowed: 'Narrowed',

    createLabel: 'How will your team use it?',
    createHint: 'You can change this any time in Settings → Features.',
  },

  auth: {
    backHome: 'Back to home',
    pitch: 'Tasks, time and leave for small teams, without the noise.',
    point1: 'Exact minutes, never rounded. Subtask time that rolls up without counting twice.',
    point2: 'Leave and public holidays that come off real capacity.',
    point3: 'Per seat, cancel any month. EU-hosted, export everything anytime.',
    signInTitle: 'Sign in',
    google: 'Continue with Google',
    googlePopupBlocked: 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.',
    googleFailed: 'Google sign-in did not complete. Try again, or use your email below.',
    orContinue: 'or with email',
    signInLede: 'Welcome back. Use your password, or ask for a link by email.',
    signUpTitle: 'Create your account',
    signUpLede: 'Free to start, no card needed. You will set up your workspace next.',
    name: 'Your name',
    email: 'Email',
    password: 'Password',
    passwordHint: 'At least 8 characters',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    currentPassword: 'Current password',
    createAccount: 'Create account',
    haveAccount: 'Already have an account?',
    noAccount: 'New here?',
    forgotPassword: 'Forgot your password?',
    resetSent: 'If that address has an account, a reset link is on its way.',
    nameRequired: 'Enter your name, at least two characters.',
    passwordTooShort: 'Use at least 8 characters.',
    passwordMismatch: 'The two passwords do not match.',
    emailTaken: 'That address already has an account. Sign in instead.',
    emailInvalid: 'That does not look like an email address.',
    wrongPassword: 'That is not your current password.',
    recentLogin: 'For safety, sign out and back in, then try again.',
    setPasswordTitle: 'Choose your password',
    setPasswordLede:
      'Your account was set up for you. Pick a password that is yours; you can change it later from your profile.',
    setPassword: 'Set password',
    skipForNow: 'Skip for now',
    changePassword: 'Change password',
    passwordChanged: 'Password changed',
    noPasswordYet: 'If you have only ever signed in with a link, use a reset link to set your first password.',
    sendResetLink: 'Email me a reset link',
    avatar: 'Photo',
    avatarHint: 'Square works best. Shown beside your name across the workspace.',
  },

  error: {
    generic: 'Something went wrong. Try again, and tell us if it keeps happening.',
    offline: 'You are offline. Changes will be saved when the connection comes back.',
    permission: 'You do not have permission to do that.',
    notFound: 'That does not exist any more. It may have been deleted.',
    suspended:
      'This workspace is read-only because the subscription has lapsed. You can still read and export everything.',
    reverted: 'That did not save, so it has been put back.',
    balanceTooLow: 'That is more leave than you have left. You have {remaining} remaining.',
    depthLimit: 'Tasks can be three levels deep. This one is already at the deepest level.',
  },

  a11y: {
    stopTimerFor: 'Stop the timer on {task}',
    passTimerFor: 'Pass the timer on {task} to a colleague',
    startTimerFor: 'Start a timer on {task}',
    expandTask: 'Show subtasks of {task}',
    collapseTask: 'Hide subtasks of {task}',
    markRead: 'Mark as read',
  },
} as const;

export type Dictionary = typeof en;
